import store, { pushSelectionUndo } from '@/store'
import { showError, showWarning, showMessage } from '@/js/utils/messageHelper'
import {
  colorNameToRgba,
  generateSegmentLabelGeoJSON,
  generateStartEndPointsFromGeoJSON,
  geojsonCreate,
  circleCreate,
  geojsonUpdate,
  getAllVertexPoints,
  setAllMidpoints,
} from '@/js/pyramid'
import { popup } from '@/js/popup'
import * as turf from '@turf/turf'
import { clickCircleSource } from '@/js/layers'
import { registerMdiIcon } from '@/js/utils/icon-registry'
// @mdi/js (2.8MB) の同期importを排除。1アイコンだけなのでパス値を直接定義
const mdiCursorMove = 'M13,6V11H18V7.75L22.25,12L18,16.25V13H13V18H16.25L12,22.25L7.75,18H11V13H6V16.25L1.75,12L6,7.75V11H11V6H7.75L12,1.75L16.25,6H13Z'
import { updatePhotoMemo } from '@/js/utils/photoMemo'

/**
 * FloatingWindowを直接開く（popup.js経由なし）
 */
export function openDrawFloatingWindow(feature) {
  if (!feature) return

  // 地物単位の編集中に別の地物を選択した場合は前の編集を終了
  if (store.state.editingFeatureId && store.state.editingFeatureId !== feature.properties?.id) {
    disableFeatureEdit()
  }
  // フリーメモポイントの場合はFWを開かない
  if (feature.properties?.isFieldNoteMemo || feature.properties?.hasMemo) return
  // currentDrawFeatureをstoreに保存
  store.commit('setCurrentDrawFeature', feature)
  // FloatingWindowを開く
  store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
}
import debounce from 'lodash/debounce'
import {
  dedupeCoords,
  featureCollectionAdd,
  highlightSpecificFeatures2025,
  highlightSpecificFeaturesCity,
  layersOnScreen,
  markerAddAndRemove,
  removeAllMarkers,
  syncFeatureCollection,
  saveDrowFeatures,
  executeMyDrawUndo,
  executeMyDrawRedo,
  pushMyDrawUndoAction,
} from '@/js/downLoad'

// =============================================================
// ヘルパー関数: source._dataからGeoJSONを安全に取得
// MapLibre 5.13.0以降、_dataがProxyオブジェクトになる場合がある
// =============================================================
export function getSourceData(source) {
  if (!source || !source._data) return null
  const rawData = source._data
  return rawData && rawData.geojson ? rawData.geojson : rawData
}

// =============================================================
// テキストクリックフラグ（App.vueのラベルクリックとdraw.jsのmapクリックの重複防止）
// =============================================================
let _textClickHandled = false
export function setTextClickHandled(val) {
  _textClickHandled = val
}

// サムネイルマーカークリックフラグ（DOMマーカーとdraw.jsのmapクリックの重複防止）
let _markerClickHandled = false
export function setMarkerClickHandled(val) {
  _markerClickHandled = val
}

// =============================================================
// 自動連番ラベル生成（ライン, ライン(1), ライン(2)...）
// =============================================================
export function getNextDefaultLabel(geometryType) {
  const baseName = {
    Point: 'ポイント',
    LineString: 'ライン',
    FreeHand: 'スケッチ',
    Polygon: 'ポリゴン',
    Circle: '円',
  }[geometryType] || geometryType

  // 既存フィーチャーを取得
  const map = store.state.map01
  if (!map) return `${baseName}(1)`

  const source = map.getSource('click-circle-source')
  if (!source) return `${baseName}(1)`

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return `${baseName}(1)`

  // 同じ種類の既存ラベルを収集
  const existingLabels = new Set()
  for (const f of geojson.features) {
    if (!f.properties || f.properties.id === 'config') continue
    // 円の場合はlabel2を使用
    const isCircle = f.properties.radius !== undefined
    const label = isCircle ? (f.properties.label2 || '') : (f.properties.label || '')
    if (label) existingLabels.add(label)
  }

  // 常に(1)から始める連番を探す
  let num = 1
  while (existingLabels.has(`${baseName}(${num})`)) {
    num++
  }
  return `${baseName}(${num})`
}

function shouldAutoOpenDrawWindow(lngLat) {
  const mode = store.state.drawAutoOpenMode
  // ブール値対応: true/'auto' → 自動開く、false/'manual' → 開かない
  const isAuto = mode === true || mode === 'auto'
  if (!isAuto) return false
  if (!lngLat) return false
  return true
}

function scheduleAutoOpenDrawWindow(handler, event, delayMs = 200) {
  if (!event?.lngLat) return
  if (!shouldAutoOpenDrawWindow(event.lngLat)) return
  setTimeout(() => handler(event), delayMs)
}

// =============================================================
// フリーハンド自己交差(=ループ)検出（Turf.js）
// =============================================================
const tempFreehandCoords = []
const tempPolygonCoords = []
let tempLineCoordsGuide = []
let tempLineCoords = []
let tempCircleCenter = null // 円の中心点
let tempCircleRadius = null // 円の半径（メートル）
let isDrawing = false

// 円のエッジドラッグ用変数
let isResizingCircle = false // 円のリサイズ中かどうか
let resizingCircleId = null // リサイズ中の円のID
let resizingCircleCenter = null // リサイズ中の円の中心座標
let resizingCircleOriginalRadius = null // 元の半径
let loopTriggered = false // 多重発火防止
let isDrawingLine = false
let lastSnapHit = null
let pendingPointerSnapHit = null
let pendingPointerSnapPreferVertex = false

// スロットリング用変数（mousemove用）
let pendingMousemoveUpdate = false
let lastMouseEvent = null
let lastMousemoveTime = 0
const MOUSEMOVE_THROTTLE_MS = 30 // スナップ判定を30msごとに制限

// フリーハンド描画用スロットリング変数
let pendingFreehandUpdate = false
let lastFreehandEvent = null
let lastFreehandTime = 0
const FREEHAND_THROTTLE_MS = 16 // フリーハンドは16msごと（約60fps）

// ラッソ描画用スロットリング変数
let pendingLassoUpdate = false
let lastLassoEvent = null
let lastLassoTime = 0
const LASSO_THROTTLE_MS = 16 // ラッソも16msごと
const LASSO_MIN_DISTANCE = 0.00001 // 最小距離閾値（度単位）

// updateGuideLine最適化用：前回のカーソル位置を記憶
let lastGuideLineCursor = null

const EPS = 1e-9
const almostEq = (a, b) => Math.abs(a - b) <= EPS
const sameXY = (p, q) => almostEq(p[0], q[0]) && almostEq(p[1], q[1])

export default function drawMethods(options = {}) {
  const { autoStopOnLoop = true } = options || {}
  const map01 = store.state.map01

  // ---------------- Utils: 正規化 / 距離 / ガイド ----------------
  function normalizeLngLat(ll) {
    let lng = ll.lng
    lng = ((((lng + 180) % 360) + 360) % 360) - 180 // [-180,180)
    return { lng, lat: ll.lat }
  }
  function pxDist(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  /**
   * クリック位置が円のエッジ付近かどうかを判定
   * @param {Object} e - マウスイベント
   * @param {number} tolerance - ピクセル単位の許容範囲
   * @returns {Object|null} - エッジ付近の円情報 {id, center, radius} or null
   */
  function getCircleEdgeAtPoint(e, tolerance = 15) {
    const source = map01.getSource(clickCircleSource.iD)
    if (!source) return null
    const geojson = getSourceData(source)
    if (!geojson?.features) return null

    const clickPoint = [e.lngLat.lng, e.lngLat.lat]

    // 円（isCircle: true または radius プロパティがある Polygon）を検索
    for (const feature of geojson.features) {
      if (feature.geometry?.type !== 'Polygon') continue
      if (!feature.properties?.radius) continue
      if (feature.properties.id?.endsWith('-point')) continue

      // 中心座標を取得：ペアのPoint feature（{id}-point）から取得、なければcanterLng/canterLatを使用
      const centerPointId = feature.properties.id + '-point'
      const centerPointFeature = geojson.features.find((f) => f.properties?.id === centerPointId)
      let center
      if (centerPointFeature && centerPointFeature.geometry?.type === 'Point') {
        center = centerPointFeature.geometry.coordinates
      } else {
        center = [feature.properties.canterLng, feature.properties.canterLat]
      }
      const radius = feature.properties.radius // メートル単位

      // クリック点と中心の距離を計算
      const distanceFromCenter = turf.distance(center, clickPoint, { units: 'meters' })

      // エッジからの距離（メートル）
      const distanceFromEdge = Math.abs(distanceFromCenter - radius)

      // ピクセル単位での許容範囲に変換（ズームレベルに依存）
      const zoom = map01.getZoom()
      const metersPerPixel = (40075016.686 * Math.cos((center[1] * Math.PI) / 180)) / Math.pow(2, zoom + 8)
      const toleranceInMeters = tolerance * metersPerPixel

      if (distanceFromEdge <= toleranceInMeters) {
        return {
          id: feature.properties.id,
          center: center,
          radius: radius,
          feature: feature,
        }
      }
    }
    return null
  }

  function ensureGuideLineSource(map) {
    if (!map.getSource('guide-line-source')) {
      map.addSource('guide-line-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    // ポリゴンの塗りつぶしレイヤーを追加（仮のポリゴンプレビュー用）
    if (!map.getLayer('guide-polygon-fill-layer')) {
      map.addLayer({
        id: 'guide-polygon-fill-layer',
        type: 'fill',
        source: 'guide-line-source',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.2,
        },
        filter: ['==', ['geometry-type'], 'Polygon'],
      })
    }
    // ポリゴンのアウトラインレイヤーを追加
    if (!map.getLayer('guide-polygon-outline-layer')) {
      map.addLayer({
        id: 'guide-polygon-outline-layer',
        type: 'line',
        source: 'guide-line-source',
        paint: {
          'line-color': '#2196F3',
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
        filter: ['==', ['geometry-type'], 'Polygon'],
      })
    }
    if (!map.getLayer('guide-line-layer')) {
      map.addLayer({
        id: 'guide-line-layer',
        type: 'line',
        source: 'guide-line-source',
        paint: { 'line-color': '#333', 'line-width': 2 },
        filter: ['==', ['geometry-type'], 'LineString'],
      })
    }
    // 座標点表示用のレイヤーを追加
    if (!map.getLayer('guide-point-layer')) {
      map.addLayer({
        id: 'guide-point-layer',
        type: 'circle',
        source: 'guide-line-source',
        paint: {
          'circle-radius': 5,
          'circle-color': '#333',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
        filter: ['==', ['geometry-type'], 'Point'],
      })
    }

    // ガイドライン距離・角度ラベル用のソースとレイヤー
    if (!map.getSource('guide-label-source')) {
      map.addSource('guide-label-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getLayer('guide-label-layer')) {
      map.addLayer({
        id: 'guide-label-layer',
        type: 'symbol',
        source: 'guide-label-source',
        layout: {
          'text-field': ['get', 'distance'],
          'text-size': 16,
          'text-offset': [0, 0.6],
          'text-anchor': 'top',
          'text-rotate': ['get', 'textBearing'],
        },
        paint: {
          'text-color': '#0000ff',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      })
    }

    // 確定ポリゴンの辺ラベル用のソースとレイヤー（面積表示ONの場合）
    if (!map.getSource('polygon-edge-label-source')) {
      map.addSource('polygon-edge-label-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getLayer('polygon-edge-label-layer')) {
      map.addLayer({
        id: 'polygon-edge-label-layer',
        type: 'symbol',
        source: 'polygon-edge-label-source',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 14,
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-rotate': ['get', 'textBearing'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': ['case', ['get', 'isAngle'], '#ff6600', '#0066cc'],
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      })
    }

    // 円描画プレビュー用のソースとレイヤー
    if (!map.getSource('circle-preview-source')) {
      map.addSource('circle-preview-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      })
    }
    if (!map.getLayer('circle-preview-fill-layer')) {
      map.addLayer({
        id: 'circle-preview-fill-layer',
        type: 'fill',
        source: 'circle-preview-source',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.2,
        },
      })
    }
    if (!map.getLayer('circle-preview-outline-layer')) {
      map.addLayer({
        id: 'circle-preview-outline-layer',
        type: 'line',
        source: 'circle-preview-source',
        paint: {
          'line-color': '#2196F3',
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      })
    }
    if (!map.getLayer('circle-preview-center-layer')) {
      map.addLayer({
        id: 'circle-preview-center-layer',
        type: 'circle',
        source: 'circle-preview-source',
        paint: {
          'circle-radius': 5,
          'circle-color': '#2196F3',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
        filter: ['==', ['geometry-type'], 'Point'],
      })
    }
    // 確定ポリゴンの辺ラベルを更新（既存データがあれば表示）
    const geojsonText = store.state.clickCircleGeojsonText
    updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
  }

  // 全マップにガイドラインソースを初期化
  function ensureGuideLineSourceAllMaps() {
    const map01 = store.state.map01
    if (map01) ensureGuideLineSource(map01)
    const otherMaps = ['map02', 'map03', 'map04']
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.getSource) {
        ensureGuideLineSource(otherMap)
      }
    })
  }

  // 全マップに円プレビューデータを設定
  function setCirclePreviewDataAllMaps(data) {
    const allMaps = ['map01', 'map02', 'map03', 'map04']
    allMaps.forEach(mapName => {
      const map = store.state[mapName]
      if (map && map.getSource) {
        const src = map.getSource('circle-preview-source')
        if (src) src.setData(data)
      }
    })
  }

  // 画面空間 最近点（辺）
  function nearestOnScreenLines(map, lines, pointPx) {
    let best = null
    const nearestOnSegPx = (A, B, P) => {
      const vx = B.x - A.x,
        vy = B.y - A.y
      const wx = P.x - A.x,
        wy = P.y - A.y
      const vv = vx * vx + vy * vy || 1e-9
      let t = (vx * wx + vy * wy) / vv
      t = Math.max(0, Math.min(1, t))
      return { x: A.x + t * vx, y: A.y + t * vy }
    }
    for (const line of lines) {
      if (!line || line.length < 2) continue
      const px = line.map(([lng, lat]) => map.project({ lng, lat }))
      for (let i = 0; i < px.length - 1; i++) {
        const Q = nearestOnSegPx(px[i], px[i + 1], pointPx)
        const d = Math.hypot(Q.x - pointPx.x, Q.y - pointPx.y)
        if (!best || d < best.d) best = { Q, d }
      }
    }
    if (!best) return null
    const lngLat = map.unproject([best.Q.x, best.Q.y])
    return { lng: lngLat.lng, lat: lngLat.lat, distPx: best.d }
  }

  // MapLibreの版差対策: 生データ取得
  function readGeojsonData(src) {
    if (!src) return null
    // MapLibre 5.13.0以降、_dataがProxyオブジェクトになる場合がある
    if (src._data) {
      const rawData = src._data
      return rawData && rawData.geojson ? rawData.geojson : rawData
    }
    if (typeof src?.getData === 'function') return src.getData()
    if (src?._options && src._options.data) return src._options.data
    return null
  }

  // 交差検出
  function detectLoopWithTurf(coords) {
    if (!coords || coords.length < 4) return null
    const n = coords.length
    const a1 = coords[n - 2]
    const a2 = coords[n - 1]
    if (sameXY(a1, a2)) return null
    const segA = turf.lineString([a1, a2])
    for (let j = 0; j <= n - 4; j++) {
      const b1 = coords[j],
        b2 = coords[j + 1]
      if (sameXY(b1, b2)) continue
      const segB = turf.lineString([b1, b2])
      const inter = turf.lineIntersect(segA, segB)
      if (inter.features.length === 0) continue
      for (const f of inter.features) {
        const p = f.geometry.coordinates
        if (sameXY(p, a1)) continue
        const mid = coords.slice(j + 1, n - 1)
        const ring = [p, ...mid, p]
        if (ring.length >= 4) {
          return { ring, meta: { intersection: p, hitOn: { j }, coords: coords.slice() } }
        }
      }
    }
    return null
  }

  // ---------------- 強力スナッパ ----------------
  function createStrongRawSnapper(
    map,
    {
      sourceIds = [], // ← スナップ対象 GeoJSON Source ID を全部
      radii = { vertexBase: 16, edgeBase: 12 }, // px
      zoomRef = 14, // ここより低ズームで半径拡大
      zoomScale = 1.25, // 1段ズームアウトごとに ×1.25
      sticky = { enable: true, radiusFactor: 1.8 }, // 例: 頂点半径×1.8以内なら保持
      self = { enable: true, getCoords: () => tempPolygonCoords },
      previewSourceId = 'snap-point-src',
      previewLayerId = 'snap-point-lyr',
      excludeSources = ['snap-point-src', 'freehand-preview-source', 'guide-line-source', 'lasso-source'],
    } = {}
  ) {
    // プレビュー点
    if (!map.getSource(previewSourceId)) {
      map.addSource(previewSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    }
    if (!map.getLayer(previewLayerId)) {
      map.addLayer({
        id: previewLayerId,
        type: 'circle',
        source: previewSourceId,
        paint: {
          'circle-radius': 6,
          'circle-color': '#22aaff',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      })
    }
    const setPreview = (lngLat) => {
      const ll = lngLat ? normalizeLngLat(lngLat) : null
      const fc = ll
        ? {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [ll.lng, ll.lat] }, properties: {} }],
          }
        : { type: 'FeatureCollection', features: [] }
      map.getSource(previewSourceId).setData(fc)
    }

    const collectVertices = (geom, push) => {
      const t = geom.type
      if (t === 'Point') push(geom.coordinates)
      else if (t === 'MultiPoint') geom.coordinates.forEach(push)
      else if (t === 'LineString') geom.coordinates.forEach(push)
      else if (t === 'MultiLineString') geom.coordinates.flat().forEach(push)
      else if (t === 'Polygon') geom.coordinates.flat().forEach(push)
      else if (t === 'MultiPolygon') geom.coordinates.flat(2).forEach(push)
    }
    const collectLines = (geom) => {
      const t = geom.type
      if (t === 'LineString') return [geom.coordinates]
      if (t === 'MultiLineString') return geom.coordinates
      if (t === 'Polygon') return geom.coordinates // 各リング
      if (t === 'MultiPolygon') return geom.coordinates.flat() // 全リング
      return []
    }

    const getRadii = () => {
      const z = map.getZoom?.() ?? zoomRef
      const k = Math.max(1, Math.pow(zoomScale, Math.max(0, zoomRef - z))) // z<zoomRef で拡大
      return { v: radii.vertexBase * k, e: radii.edgeBase * k, k }
    }

    const getSnap = (pointPx, { first = null, preferVertex = false, lastSnapHit = null } = {}) => {
      const { v: tolV, e: tolE } = getRadii()

      // スティッキー
      if (sticky?.enable && lastSnapHit?.lngLat) {
        const dPrev = pxDist(map.project(lastSnapHit.lngLat), pointPx)
        const keepR = (lastSnapHit.type?.startsWith('vertex') ? tolV : tolE) * (sticky.radiusFactor ?? 1.5)
        if (dPrev <= keepR) {
          setPreview(lastSnapHit.lngLat)
          return lastSnapHit
        }
      }

      let bestVertex = null
      let bestEdge = null

      const considerV = (lngLat, type = 'vertex') => {
        const d = pxDist(map.project(lngLat), pointPx)
        if (d <= tolV && (!bestVertex || d < bestVertex.distPx)) {
          bestVertex = { lngLat, type, distPx: d }
        }
      }
      const considerE = (lngLat, type = 'edge') => {
        const d = pxDist(map.project(lngLat), pointPx)
        if (d <= tolE && (!bestEdge || d < bestEdge.distPx)) {
          bestEdge = { lngLat, type, distPx: d }
        }
      }

      // 0) 先頭点で閉じる
      if (first) considerV(first, 'first')

      // 1) GeoJSON生データ
      for (const sid of sourceIds) {
        if (excludeSources.includes(sid)) continue
        const src = map.getSource(sid)
        if (!src) continue

        const raw = readGeojsonData(src)
        if (!raw) continue

        const fc =
          raw.type === 'FeatureCollection'
            ? raw
            : { type: 'FeatureCollection', features: Array.isArray(raw.features) ? raw.features : [] }

        for (const f of fc.features || []) {
          const g = f?.geometry
          if (!g) continue
          // 頂点
          collectVertices(g, (c) => considerV({ lng: c[0], lat: c[1] }, 'vertex'))
          // 辺
          if (!preferVertex) {
            const lines = collectLines(g)
            const ll = nearestOnScreenLines(map, lines, pointPx)
            if (ll) considerE({ lng: ll.lng, lat: ll.lat }, 'edge')
          }
        }
      }

      // 2) 自分自身（描画中リング）
      if (self?.enable) {
        const coords = (self.getCoords?.() || []).slice()
        if (coords.length) {
          for (const c of coords) considerV({ lng: c[0], lat: c[1] }, 'self-vertex')
          if (!preferVertex && coords.length >= 2) {
            const ringClosed =
              coords[0][0] === coords.at(-1)[0] && coords[0][1] === coords.at(-1)[1]
                ? coords
                : coords.concat([coords[0]])
            const ll = nearestOnScreenLines(map, [ringClosed], pointPx)
            if (ll) considerE({ lng: ll.lng, lat: ll.lat }, 'self-edge')
          }
        }
      }

      const result = bestVertex || (preferVertex ? null : bestEdge) || null
      setPreview(result?.lngLat || null)
      return result
    }

    return { getSnap, setPreview, getRadii }
  }

  // ---------------- Pointer handlers: Freehand ----------------
  map01.getCanvas().addEventListener('pointerdown', (e) => {
    if (!store.state.isDrawFree) return
    // 投げ縄モード、編集・移動モード、投げ縄選択済み状態、またはドラッグ中は描画をブロック
    if (
      store.state.isDrawLasso ||
      store.state.editEnabled ||
      store.state.isLassoSelected ||
      store.state._isDraggingFeature
    )
      return

    const point = map01.unproject([e.clientX, e.clientY])
    isDrawing = true
    loopTriggered = false

    tempFreehandCoords.length = 0
    tempFreehandCoords.push([point.lng, point.lat])

    map01.getCanvas().style.cursor = 'crosshair'
    map01.dragPan.disable()
  })

  map01.getCanvas().addEventListener('pointermove', (e) => {
    if (!isDrawing) return

    lastFreehandEvent = e

    if (pendingFreehandUpdate) return

    pendingFreehandUpdate = true
    requestAnimationFrame(() => {
      pendingFreehandUpdate = false

      if (!lastFreehandEvent) return

      // タイムベースのスロットリング
      const now = performance.now()
      if (now - lastFreehandTime < FREEHAND_THROTTLE_MS) {
        return
      }
      lastFreehandTime = now

      const event = lastFreehandEvent

      const pt = map01.unproject([event.clientX, event.clientY])
      const xy = [pt.lng, pt.lat]

      const last = tempFreehandCoords[tempFreehandCoords.length - 1]
      if (!last || !sameXY(last, xy)) tempFreehandCoords.push(xy)

      const previewSrc = map01.getSource('freehand-preview-source')
      if (previewSrc) {
        previewSrc.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: tempFreehandCoords },
              properties: {
                keiko: 0,
                color: store.state.currentFreeHandColor || 'black',
                'keiko-color': store.state.currentFreeHandKeikoColor || '#1C1C1C',
                'line-width': store.state.currentFreeHandWidth || 5,
              },
            },
          ],
        })
      }

      if (!loopTriggered && !store.state.elevationDrawMode) {
        const hit = detectLoopWithTurf(tempFreehandCoords)
        if (hit) {
          loopTriggered = true

          const ring = hit.ring
          const p = hit.meta.intersection

          // フリーハンドを閉じてポリゴンにする設定がONの場合のみ停止してポリゴン作成
          if (store.state.closeLineToPolygon && autoStopOnLoop) {
            isDrawing = false
            map01.getCanvas().style.cursor = ''
            map01.dragPan.enable()
            finishPreview()

            const id = String(Math.floor(10000 + Math.random() * 90000))
            store.state.id = id

            const coords = [ring]
            // 前回のスタイルを引き継ぐ
            const lastStyle = store.state.lastPolygonStyle || {}
            const colorName = lastStyle.colorName || store.state.currentPolygonColor || 'black'
            const alpha = lastStyle.opacity ?? 0.6
            const properties = {
              id,
              pairId: id,
              label: getNextDefaultLabel('Polygon'),
              color: colorNameToRgba(colorName, alpha),
              colorName: colorName,
              'line-width': lastStyle['line-width'] || 3,
              opacity: alpha,
              isArea: store.state.currentPolygonAreaCheck || false,
            }
            geojsonCreate(map01, 'Polygon', coords, properties)
            // 新規作成した地物を強調表示
            highlightSelectedFeature(map01, id)

            const dummyEvent = { lngLat: { lng: p[0], lat: p[1] } }
            store.state.coordinates = [p[0], p[1]]
            scheduleAutoOpenDrawWindow(onLineClick, dummyEvent, 500)

            tempFreehandCoords.length = 0
          }
          // 設定がOFFの場合は何もせず描画を続行（loopTriggeredフラグだけ立てる）
        }
      }
    })
  })

  map01.getCanvas().addEventListener('pointerup', () => {
    if (!isDrawing) return
    isDrawing = false
    map01.getCanvas().style.cursor = ''
    map01.dragPan.enable()

    // loopTriggeredがtrueでもcloseLineToPolygonがfalseならLineStringとして作成
    if (tempFreehandCoords.length >= 2 && (!loopTriggered || !store.state.closeLineToPolygon)) {
      const id = String(Math.floor(10000 + Math.random() * 90000))
      store.state.id = id

      // 前回のスタイルを引き継ぐ（ラベルは常に新規生成）
      const lastStyle = store.state.lastFreeHandStyle || {}
      const properties = {
        id,
        'free-hand': 1,
        keiko: lastStyle.keiko !== undefined ? lastStyle.keiko : 0,
        label: store.state.elevationDrawMode ? '' : getNextDefaultLabel('FreeHand'),
        color: lastStyle.color || store.state.currentFreeHandColor || 'black',
        'keiko-color': lastStyle['keiko-color'] || store.state.currentFreeHandKeikoColor || 'rgba(28,28,28,0.5)',
        offsetValue: [0.6, 0],
        'line-width': lastStyle['line-width'] || store.state.currentFreeHandWidth || 5,
        textAnchor: 'left',
        textJustify: 'left',
      }

      const createdFeature = geojsonCreate(map01, 'FreeHand', tempFreehandCoords.slice(), properties)
      // 新規作成した地物を強調表示
      highlightSelectedFeature(map01, id)

      // 標高図モードの場合はfinishDrawing()を呼び出す
      if (store.state.elevationDrawMode === 'free') {
        finishDrawing(createdFeature)
      } else {
        store.state.coordinates = tempFreehandCoords[0]
        const ev = { lngLat: { lng: tempFreehandCoords[0][0], lat: tempFreehandCoords[0][1] } }
        scheduleAutoOpenDrawWindow(onLineClick, ev, 500)
      }
    } else {
    }

    finishPreview()
    tempFreehandCoords.length = 0
  })

  map01.getCanvas().addEventListener('pointerleave', () => {
    if (!isDrawing) return
    isDrawing = false
    map01.dragPan.enable()
    map01.getCanvas().style.cursor = ''
    tempFreehandCoords.length = 0
    finishPreview()
  })

  // ---------------- Lasso（既存そのまま） ----------------
  let isLassoDrawing = false
  let lassoCoords = []
  const lassoSourceId = 'lasso-source'
  const lassoLayerId = 'lasso-layer'

  map01.getCanvas().addEventListener('pointerdown', (e) => {
    if (!store.state.isDrawLasso && !store.state.isDrawLassoForTokizyo && !store.state.isDrawLassoForTokizyo2026 && !store.state.isDrawLassoForChibanzu) return
    // Transform Boxがアクティブな時は投げ縄描画を開始しない（ハンドル操作を優先）
    if (transformBoxState.isActive) return
    if (!map01.getSource(lassoSourceId)) {
      map01.addSource(lassoSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map01.addLayer({
        id: lassoLayerId,
        type: 'line',
        source: lassoSourceId,
        paint: { 'line-color': 'rgba(0,0,255,0.6)', 'line-width': 10 },
      })
    }
    map01.dragPan.disable()
    isLassoDrawing = true
    lassoCoords = []
    map01.getCanvas().style.cursor = 'crosshair'
  })

  map01.getCanvas().addEventListener('pointermove', (e) => {
    if (!isLassoDrawing) return

    lastLassoEvent = e

    if (pendingLassoUpdate) return

    pendingLassoUpdate = true
    requestAnimationFrame(() => {
      pendingLassoUpdate = false

      if (!lastLassoEvent) return

      // タイムベースのスロットリング
      const now = performance.now()
      if (now - lastLassoTime < LASSO_THROTTLE_MS) {
        return
      }
      lastLassoTime = now

      const event = lastLassoEvent
      // 印刷モード対応: getBoundingClientRect()を使用して正しい座標を取得
      const rect = map01.getCanvas().getBoundingClientRect()
      const point = map01.unproject([event.clientX - rect.left, event.clientY - rect.top])

      // 距離チェック：前回の座標と近すぎる場合はスキップ
      if (lassoCoords.length > 0) {
        const last = lassoCoords[lassoCoords.length - 1]
        const dx = Math.abs(point.lng - last[0])
        const dy = Math.abs(point.lat - last[1])
        if (dx < LASSO_MIN_DISTANCE && dy < LASSO_MIN_DISTANCE) {
          return
        }
      }

      lassoCoords.push([point.lng, point.lat])

      const lassoSource = map01.getSource(lassoSourceId)
      if (lassoSource) {
        lassoSource.setData({
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: lassoCoords } }],
        })
      }
    })
  })

  map01.getCanvas().addEventListener('pointerup', async (e) => {
    if (!isLassoDrawing) return
    isLassoDrawing = false
    map01.getCanvas().style.cursor = ''
    if (lassoCoords.length >= 3) {
      lassoCoords.push(lassoCoords[0])
      const polygon = turf.polygon([lassoCoords])
      if (store.state.isDraw) {
        const source = map01.getSource(clickCircleSource.iD)
        const geojson = getSourceData(source)
        if (!geojson || !geojson.features) return
        // 投げ縄選択前のgeojsonをバックアップ（キャンセル用）
        store.state.lassoBackupGeojson = JSON.stringify(geojson)
        let isLassoSelected = false
        const [pMinX, pMinY, pMaxX, pMaxY] = turf.bbox(polygon)
        // 1. まず投げ縄と交差する地物を選択
        geojson.features.forEach((feature) => {
          feature.properties.lassoSelected = false
          if (feature.geometry) {
            const [fMinX, fMinY, fMaxX, fMaxY] = turf.bbox(feature)
            if (fMaxX < pMinX || fMinX > pMaxX || fMaxY < pMinY || fMinY > pMaxY) return
            if (turf.booleanIntersects(feature, polygon)) {
              feature.properties.lassoSelected = true
              isLassoSelected = true
            }
          }
        })
        // 2. 選択された地物のペア（円の中心点、線の矢印など）も選択する
        const selectedIds = new Set()
        geojson.features.forEach((feature) => {
          if (feature.properties.lassoSelected) {
            selectedIds.add(feature.properties.id)
          }
        })
        geojson.features.forEach((feature) => {
          if (feature.properties.lassoSelected) return // 既に選択済み
          const id = feature.properties.id
          const pairId = feature.properties.pairId
          // 円の中心点（{parentId}-point形式）
          if (id && id.endsWith('-point')) {
            const parentId = id.replace(/-point$/, '')
            if (selectedIds.has(parentId)) {
              feature.properties.lassoSelected = true
            }
          }
          // 線の矢印など（pairIdで紐付け）
          if (pairId && selectedIds.has(pairId)) {
            feature.properties.lassoSelected = true
          }
        })
        store.state.lassoGeojson = JSON.stringify(
          turf.featureCollection(geojson.features.filter((f) => f.properties.lassoSelected === true))
        )
        store.state.isLassoSelected = !!isLassoSelected
        source.setData(geojson)
        store.state.clickCircleGeojsonText = JSON.stringify(geojson)
        clickCircleSource.obj.data = geojson
        await saveDrowFeatures(geojson.features)
        // Transform Boxを表示
        if (isLassoSelected) {
          await showTransformBox()
        }
      } else if (store.state.isDrawLassoForTokizyo) {
        pushSelectionUndo(store)
        store.state.lastHighlightedChiban = null // ⭐️これを追加
        const layerId = 'oh-homusyo-2025-polygon'
        const intersectsFeatures = await getIntersectsFeatures(map01, layerId, polygon)
        store.state.highlightedChibans = new Set()
        intersectsFeatures.forEach((f) => {
          const targetId = `${f.properties['筆ID']}_${f.properties['地図名']}`
          store.state.highlightedChibans.add(targetId)
        })
        highlightSpecificFeatures2025(map01, layerId)
        // 投げ縄完了時に右ドロワーを開く
        if (intersectsFeatures.length > 0) {
          store.state.popupFeatureProperties = intersectsFeatures[0].properties
          store.state.rightDrawerTitle = '2025登記所地図'
          store.commit('setRightDrawer', true)
        }
        store.state.isDrawLassoForTokizyo = false
      } else if (store.state.isDrawLassoForTokizyo2026) {
        pushSelectionUndo(store)
        store.state.lastHighlightedChiban = null
        const layerId2026 = 'oh-homusyo-2025-fill'
        const intersectsFeatures2026 = await getIntersectsFeatures(map01, layerId2026, polygon)
        const newSet = new Set()
        const newFeatures = new Map()
        store.state.highlightedFeatures?.forEach((v, k) => {
          if (!k.endsWith('_tokizyo')) { newSet.add(k); newFeatures.set(k, v) }
        })
        intersectsFeatures2026.forEach((f) => {
          const targetId = `${f.properties['地図番号']}_${f.properties['筆ID']}_${f.properties['地番']}_tokizyo`
          newSet.add(targetId)
          newFeatures.set(targetId, { type: 'Feature', geometry: f.geometry, properties: { ...f.properties, _targetId: targetId } })
        })
        store.state.highlightedChibans = newSet
        store.state.highlightedFeatures = newFeatures
        if (map01.getLayer(layerId2026)) {
          const selectedKeys = Array.from(newSet).filter(k => k.endsWith('_tokizyo'))
          const concatExpr = ['concat', ['get', '地図番号'], '_', ['get', '筆ID'], '_', ['get', '地番'], '_tokizyo']
          const colorExpr = selectedKeys.length > 0
            ? ['case', ['in', concatExpr, ['literal', selectedKeys]], 'rgba(60, 120, 220, 0.45)', 'rgba(0, 0, 0, 0)']
            : 'rgba(0, 0, 0, 0)'
          map01.setPaintProperty(layerId2026, 'fill-color', colorExpr)
        }
        // 投げ縄完了時に右ドロワーを開く
        if (intersectsFeatures2026.length > 0) {
          store.state.popupFeatureProperties = intersectsFeatures2026[0].properties
          store.state.rightDrawerTitle = '2026登記所地図テスト'
          store.commit('setRightDrawer', true)
        }
        store.state.isDrawLassoForTokizyo2026 = false
      } else if (store.state.isDrawLassoForChibanzu) {
        const layers = layersOnScreen(map01)
        const layerId = layers.filter((layer) => layer.id.includes('oh-chiban'))[0]?.id
        if (layerId) {
          pushSelectionUndo(store)
          const intersectsFeatures = await getIntersectsFeatures(map01, layerId, polygon)
          store.state.highlightedChibans = new Set()
          intersectsFeatures.forEach((f) => {
            // Shozai + Chiban がある場合はそれを優先（浜松市FGB対応）
            let targetId
            if (f.properties['Shozai'] !== undefined && f.properties['Chiban'] !== undefined) {
              targetId = `${f.properties['Shozai']}_${f.properties['Chiban']}`
            } else if (layerId.includes('oh-chibanzu')) {
              targetId = `${f.properties['id']}`
            } else {
              targetId = `${f.properties['oh3id']}`
            }
            store.state.highlightedChibans.add(targetId)
          })
          highlightSpecificFeaturesCity(map01, layerId)
          // 投げ縄完了時に右ドロワーを開く（地番図）
          if (intersectsFeatures.length > 0) {
            store.state.popupFeatureProperties = intersectsFeatures[0].properties
            store.state.rightDrawerTitle = '地番図'
            store.commit('setRightDrawer', true)
          }
        }
      }
    }
    const lassoSource = map01.getSource(lassoSourceId)
    if (lassoSource) {
      lassoSource.setData({ type: 'FeatureCollection', features: [] })
    }
    if (map01.getLayer(lassoLayerId)) {
      map01.removeLayer(lassoLayerId)
    }
    if (map01.getSource(lassoSourceId)) {
      map01.removeSource(lassoSourceId)
    }
    map01.dragPan.enable()
  })

  // ---------------- スナップ（強力版） ----------------
  function initSnapper() {
    const snap = createStrongRawSnapper(map01, {
      sourceIds: [clickCircleSource.iD], // ←必要な GeoJSON Source を全部
      radii: { vertexBase: 10, edgeBase: 8 },
      zoomRef: 14,
      zoomScale: 1.25,
      sticky: { enable: true, radiusFactor: 1.3 },
      self: { enable: true, getCoords: () => tempPolygonCoords },
      excludeSources: ['snap-point-src', 'freehand-preview-source', 'guide-line-source', 'lasso-source'],
    })

    const mapCanvas = map01.getCanvas()
    const getPointFromPointerEvent = (event) => {
      const rect = mapCanvas.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    }

    mapCanvas.addEventListener('pointerdown', (event) => {
      if (!store.state.isDrawPolygon && !store.state.isDrawLine) {
        pendingPointerSnapHit = null
        pendingPointerSnapPreferVertex = false
        return
      }

      const preferVertex = !!event.shiftKey
      const point = getPointFromPointerEvent(event)
      pendingPointerSnapHit =
        snap.getSnap(point, {
          first: null,
          preferVertex,
          lastSnapHit,
        }) || null
      pendingPointerSnapPreferVertex = preferVertex
    })

    function onPolygonClick(e) {

      // 標高図作成モード中は何もしない
      if (store.state.elevationDrawMode) {
        return
      }

      // FeatureIDから該当のFeatureを取得
      const featureId = store.state.id
      if (!featureId) return

      // すぐにFWを開く（処理完了を待たない）
      const source = map01.getSource(clickCircleSource.iD)
      const geojson = getSourceData(source)

      // Featureがまだ存在しない場合は仮のFeatureを作成
      let feature = geojson?.features?.find((f) => f.properties?.id === featureId)

      if (!feature) {
        // 仮のFeatureを作成（後で更新される）
        feature = {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [[]] },
          properties: { id: featureId },
        }
      }

      // すぐにFWを開く
      openDrawFloatingWindow(feature)
    }

    function onCircleClick(e) {

      // FeatureIDから該当のFeatureを取得
      const featureId = store.state.id
      if (!featureId) return

      // すぐにFWを開く（処理完了を待たない）
      const source = map01.getSource(clickCircleSource.iD)
      const geojson = getSourceData(source)

      // Featureがまだ存在しない場合は仮のFeatureを作成
      let feature = geojson?.features?.find((f) => f.properties?.id === featureId)

      if (!feature) {
        // 仮のFeatureを作成（後で更新される）
        feature = {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [[]] },
          properties: { id: featureId, isCircle: true },
        }
      }

      // すぐにFWを開く
      openDrawFloatingWindow(feature)
    }

    // mousedown: 円のエッジドラッグ開始を検出（編集モード時のみ）
    map01.on('mousedown', (e) => {
      // 編集モードでない場合は無効
      if (!store.state.editEnabled) {
        return
      }

      // 投げ縄モード、投げ縄選択済み、またはドラッグ中は円の半径操作を無視
      if (store.state.isDrawLasso || store.state.isLassoSelected || store.state._isDraggingFeature) {
        return
      }

      // 円のエッジ付近をクリックしたかチェック
      const circleEdge = getCircleEdgeAtPoint(e, 15)
      if (circleEdge) {
        // 円のエッジドラッグを開始
        isResizingCircle = true
        resizingCircleId = circleEdge.id
        resizingCircleCenter = circleEdge.center
        resizingCircleOriginalRadius = circleEdge.radius

        // ドラッグパンを無効化
        map01.dragPan.disable()

        // カーソルを変更（CSSクラスで設定）
        map01.getCanvas().classList.add('circle-edge-resize')
        // force-pointerクラスを削除（popup.jsが追加する）
        map01.getCanvas().classList.remove('force-pointer')

        // プレビューソースを確保（全マップ）
        ensureGuideLineSourceAllMaps()

        e.preventDefault()
      }
    })

    // mouseup: 円のエッジドラッグ終了
    map01.on('mouseup', (e) => {
      if (!isResizingCircle) return

      // 投げ縄選択済みまたはドラッグ中は処理しない（安全策）
      if (store.state.isLassoSelected || store.state._isDraggingFeature) {
        isResizingCircle = false
        resizingCircleId = null
        resizingCircleCenter = null
        resizingCircleOriginalRadius = null
        return
      }

      // 新しい半径を計算
      const newRadius = turf.distance(resizingCircleCenter, [e.lngLat.lng, e.lngLat.lat], { units: 'meters' })

      if (newRadius >= 1) {
        // 円を更新（geojsonUpdateを使用）
        const map = store.state.map01
        const source = map.getSource(clickCircleSource.iD)
        const geojson = getSourceData(source)
        const feature = geojson?.features?.find((f) => f.properties?.id === resizingCircleId)

        if (feature) {
          // 中心座標が正しいことを確認し、必要なら更新
          // resizingCircleCenterはgetCircleEdgeAtPointでペアのPoint featureから取得した正確な中心
          if (resizingCircleCenter) {
            feature.properties.canterLng = resizingCircleCenter[0]
            feature.properties.canterLat = resizingCircleCenter[1]
          }

          const text = feature.properties.label2 || ''
          store.state.clickCircleGeojsonText = geojsonUpdate(
            map,
            'Circle',
            clickCircleSource.iD,
            resizingCircleId,
            'label2',
            text,
            Math.floor(newRadius)
          )

          // マーカーを更新
          markerAddAndRemove(true)

          // パーマリンク更新
          store.state.updatePermalinkFire = !store.state.updatePermalinkFire
        }
      }

      // プレビューをクリア（全マップ）
      setCirclePreviewDataAllMaps({ type: 'FeatureCollection', features: [] })

      // ツールチップを非表示
      hideCircleRadiusTooltip()

      // 状態をリセット
      isResizingCircle = false
      resizingCircleId = null
      resizingCircleCenter = null
      resizingCircleOriginalRadius = null

      // ドラッグパンを再有効化
      map01.dragPan.enable()

      // カーソルをリセット（CSSクラスを削除）
      map01.getCanvas().classList.remove('circle-edge-resize')
    })

    // click: 座標を追加（全デバイス - MapLibreが自動でタップをclickに変換）
    map01.on('click', (e) => {
      // 円描画モード（2クリック：1回目で中心、2回目で半径）
      // 投げ縄モード、編集・移動モード、投げ縄選択済み状態、またはドラッグ中は円の作成をスキップ
      if (
        store.state.isDrawCircle &&
        !store.state.editEnabled &&
        !store.state.isDrawLasso &&
        !store.state.isLassoSelected &&
        !store.state._isDraggingFeature
      ) {
        // ガイドラインソースと円プレビューソースを初期化（全マップ）
        ensureGuideLineSourceAllMaps()

        if (!tempCircleCenter) {
          // 1クリック目：中心点を設定
          tempCircleCenter = [e.lngLat.lng, e.lngLat.lat]

          // 中心点のプレビューを表示（全マップ）
          setCirclePreviewDataAllMaps({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: tempCircleCenter },
                properties: {},
              },
            ],
          })
        } else {
          // 2クリック目：半径を確定して円を作成
          const radiusM = turf.distance(tempCircleCenter, [e.lngLat.lng, e.lngLat.lat], { units: 'meters' })

          if (radiusM < 1) {
            return
          }

          const id = String(Math.floor(10000 + Math.random() * 90000))
          store.state.id = id

          // 前回のスタイルを引き継ぐ（label2, showRadius, 色）
          const lastStyle = store.state.lastCircleStyle || {}
          const colorName = lastStyle.colorName || store.state.currentCircleColor || 'black'
          const showRadius = lastStyle.showRadius !== false // デフォルトtrue
          const alpha = lastStyle.opacity ?? 0.6
          const radiusInt = Math.floor(radiusM)
          const properties = {
            id,
            pairId: id,
            label: showRadius ? radiusInt : '', // showRadius=falseなら半径ラベル非表示
            label2: getNextDefaultLabel('Circle'), // 自動連番
            radius: radiusInt,
            canterLng: tempCircleCenter[0],
            canterLat: tempCircleCenter[1],
            color: lastStyle.color || colorNameToRgba(colorName, alpha),
            colorName: colorName,
            lineColor: 'black', // 縁の色（編集モード強調のリセット用）
            'line-width': lastStyle['line-width'] || 1,
            opacity: alpha,
            'fill-opacity': alpha,
            isCircle: true,
            showRadius: showRadius,
          }

          geojsonCreate(map01, 'Circle', tempCircleCenter, properties, Math.floor(radiusM))
          // 新規作成した地物を強調表示
          highlightSelectedFeature(map01, id)

          // 作成した円のスタイルをlastCircleStyleに保存
          // これにより、次の円作成時に確実にこの色が引き継がれる
          store.state.lastCircleStyle = {
            color: properties.color,
            colorName: properties.colorName,
            'line-width': properties['line-width'],
            showRadius: properties.showRadius,
            label2: properties.label2,
            opacity: properties.opacity,
            'fill-opacity': properties['fill-opacity'],
          }

          // プレビューをクリア（全マップ）
          setCirclePreviewDataAllMaps({ type: 'FeatureCollection', features: [] })

          // ツールチップを非表示
          hideCircleRadiusTooltip()

          // FWを開く（座標を先に保存してからリセット）
          const centerForFW = [...tempCircleCenter]
          scheduleAutoOpenDrawWindow(onCircleClick, { lngLat: { lng: centerForFW[0], lat: centerForFW[1] } }, 500)

          // リセット
          tempCircleCenter = null
          tempCircleRadius = null
        }
        return
      }

      if (!store.state.isDrawPolygon && !store.state.isDrawLine) return
      // 投げ縄モード、編集・移動モード、投げ縄選択済み状態、またはドラッグ中は描画をブロック
      if (
        store.state.isDrawLasso ||
        store.state.editEnabled ||
        store.state.isLassoSelected ||
        store.state._isDraggingFeature
      )
        return

      // 最初の点へのスナップは無効
      const firstLL = null

      const preferVertex = !!e.originalEvent?.shiftKey
      let hit = pendingPointerSnapHit
      if (!hit || pendingPointerSnapPreferVertex !== preferVertex || !hit.lngLat) {
        hit = snap.getSnap(e.point, { first: firstLL, preferVertex, lastSnapHit })
      }
      pendingPointerSnapHit = null
      pendingPointerSnapPreferVertex = false
      lastSnapHit = hit || lastSnapHit
      const ll = normalizeLngLat(hit?.lngLat || e.lngLat)
      const p = [ll.lng, ll.lat]

      if (store.state.isDrawPolygon) {
        const last = tempPolygonCoords[tempPolygonCoords.length - 1]
        if (!last || last[0] !== p[0] || last[1] !== p[1]) {
          tempPolygonCoords.push(p)
          // 座標追加後、プレビューを更新（黒丸表示を含む）
          updateGuideLine(map01, tempPolygonCoords, ll)
          // 確定ボタンを表示
          if (tempPolygonCoords.length >= 2) {
            store.state.showDrawConfrim = true
          }
        }
      }

      if (store.state.isDrawLine) {
        const last = tempLineCoords[tempLineCoords.length - 1]
        if (!last || last[0] !== p[0] || last[1] !== p[1]) {
          tempLineCoords.push(p)
          // 座標追加後、プレビューを更新（黒丸表示を含む）
          updateGuideLine(map01, tempLineCoords, ll)
          // 確定ボタンを表示
          if (tempLineCoords.length >= 2) {
            store.state.showDrawConfrim = true
          }
        }
      }

      // ガイド始点も同じ点で（全マップ）
      ensureGuideLineSourceAllMaps()
      const lastG = tempLineCoordsGuide[tempLineCoordsGuide.length - 1]
      if (!lastG || lastG[0] !== p[0] || lastG[1] !== p[1]) {
        tempLineCoordsGuide.push(p)
      }
    })

    // dblclick: ポリゴン/ライン確定
    map01.on('dblclick', (e) => {
      if (!store.state.isDrawPolygon && !store.state.isDrawLine) return
      e.preventDefault()

      // ポリゴン確定
      if (store.state.isDrawPolygon && tempPolygonCoords.length >= 3) {
        if (
          tempPolygonCoords.length < 4 ||
          tempPolygonCoords[0][0] !== tempPolygonCoords.at(-1)[0] ||
          tempPolygonCoords[0][1] !== tempPolygonCoords.at(-1)[1]
        ) {
          tempPolygonCoords.push([...tempPolygonCoords[0]])
        }
        const coords = [tempPolygonCoords.slice()]
        const id = String(Math.floor(10000 + Math.random() * 90000))
        store.state.id = id
        // 前回のスタイルを引き継ぐ
        const lastStyle = store.state.lastPolygonStyle || {}
        const colorName = lastStyle.colorName || store.state.currentPolygonColor || 'black'
        const alpha = lastStyle.opacity ?? 0.6
        const properties = {
          id,
          pairId: id,
          label: getNextDefaultLabel('Polygon'),
          color: colorNameToRgba(colorName, alpha),
          colorName: colorName,
          'line-width': lastStyle['line-width'] || 1,
          opacity: alpha,
          'fill-opacity': alpha,
          isArea: store.state.currentPolygonAreaCheck || false,
        }
        geojsonCreate(map01, 'Polygon', coords, properties)
        // 新規作成した地物を強調表示
        highlightSelectedFeature(map01, id)
        // playDrawCompletionEffect(map01, coords, 'Polygon'); // パルスエフェクト

        const dummyEvent = { lngLat: { lng: tempPolygonCoords[0][0], lat: tempPolygonCoords[0][1] } }
        store.state.coordinates = [tempPolygonCoords[0][0], tempPolygonCoords[0][1]]
        scheduleAutoOpenDrawWindow(onPolygonClick, dummyEvent, 500)

        finishPreview()
        tempPolygonCoords.length = 0
        tempLineCoordsGuide.length = 0
        snap.setPreview(null)
        lastSnapHit = null
        // 確定ポリゴンの辺ラベルを更新
        const geojsonText = store.state.clickCircleGeojsonText
        updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
      }

      // ライン確定
      if (store.state.isDrawLine && tempLineCoords.length >= 2) {
        // finishDrawing()を呼び出して標高図モード対応
        finishDrawing()
      }
    })

    // mousemove: スナップ判定 & プレビュー更新（描画中のみ）
    // requestAnimationFrame + タイムベーススロットリング
    map01.on('mousemove', (e) => {
      // 円のエッジドラッグ中：プレビュー更新（全マップ）
      if (isResizingCircle && resizingCircleCenter) {
        const radiusM = turf.distance(resizingCircleCenter, [e.lngLat.lng, e.lngLat.lat], { units: 'meters' })

        if (radiusM >= 1) {
          const features = circleCreate(resizingCircleCenter[0], resizingCircleCenter[1], radiusM)
          setCirclePreviewDataAllMaps({
            type: 'FeatureCollection',
            features: [
              features.circle,
              { type: 'Feature', geometry: { type: 'Point', coordinates: resizingCircleCenter }, properties: {} },
            ],
          })

          // リアルタイム半径表示
          updateRealtimeInfo([resizingCircleCenter, [e.lngLat.lng, e.lngLat.lat]], 'circle', radiusM)

          // ツールチップをマウス位置に表示
          showCircleRadiusTooltip(e.originalEvent, Math.floor(radiusM))
        }
        return
      }

      // 円のエッジホバー時：カーソルを変更（編集モード時のみ）
      if (store.state.editEnabled) {
        const circleEdge = getCircleEdgeAtPoint(e, 15)
        if (circleEdge) {
          // CSSクラスでカーソルを設定（!importantで上書き）
          map01.getCanvas().classList.add('circle-edge-resize')
          // force-pointerクラスを一時的に削除（popup.jsが追加する）
          map01.getCanvas().classList.remove('force-pointer')
        } else {
          map01.getCanvas().classList.remove('circle-edge-resize')
        }
      }

      // 円描画モード：中心点設定後のプレビュー（全マップ）
      if (store.state.isDrawCircle && tempCircleCenter) {
        const radiusM = turf.distance(tempCircleCenter, [e.lngLat.lng, e.lngLat.lat], { units: 'meters' })

        if (radiusM >= 1) {
          const features = circleCreate(tempCircleCenter[0], tempCircleCenter[1], radiusM)
          setCirclePreviewDataAllMaps({
            type: 'FeatureCollection',
            features: [
              features.circle,
              { type: 'Feature', geometry: { type: 'Point', coordinates: tempCircleCenter }, properties: {} },
            ],
          })

          // リアルタイム半径表示
          updateRealtimeInfo([tempCircleCenter, [e.lngLat.lng, e.lngLat.lat]], 'circle', radiusM)

          // ツールチップをマウス位置に表示
          showCircleRadiusTooltip(e.originalEvent, Math.floor(radiusM))
        }
        return
      }

      if (!store.state.isDrawPolygon && !store.state.isDrawLine) {
        snap.setPreview(null)
        lastSnapHit = null
        pendingPointerSnapHit = null
        pendingPointerSnapPreferVertex = false
        return
      }

      lastMouseEvent = e

      if (pendingMousemoveUpdate) return

      pendingMousemoveUpdate = true
      requestAnimationFrame(() => {
        pendingMousemoveUpdate = false

        if (!lastMouseEvent) return

        // タイムベースのスロットリング：30ms以内は処理をスキップ
        const now = performance.now()
        if (now - lastMousemoveTime < MOUSEMOVE_THROTTLE_MS) {
          return
        }
        lastMousemoveTime = now

        const event = lastMouseEvent

        // 最初の点へのスナップは無効
        const firstLL = null

        const preferVertex = !!event.originalEvent?.shiftKey
        lastSnapHit = snap.getSnap(event.point, { first: firstLL, preferVertex, lastSnapHit })
        pendingPointerSnapHit = lastSnapHit
        pendingPointerSnapPreferVertex = preferVertex

        // ガイドもスナップ位置で更新
        const cursor = lastSnapHit?.lngLat ? normalizeLngLat(lastSnapHit.lngLat) : event.lngLat
        if (store.state.isDrawPolygon) {
          updateGuideLine(map01, tempPolygonCoords, cursor)
          // リアルタイム面積表示を更新（カーソル位置を含む）
          const coordsWithCursor = [...tempPolygonCoords, [cursor.lng, cursor.lat]]
          updateRealtimeInfo(coordsWithCursor, 'polygon')
        } else if (store.state.isDrawLine) {
          updateGuideLine(map01, tempLineCoordsGuide, cursor)
          // リアルタイム距離表示を更新（カーソル位置を含む）
          const coordsWithCursor = [...tempLineCoordsGuide, [cursor.lng, cursor.lat]]
          updateRealtimeInfo(coordsWithCursor, 'line')
        }
      })
    })
  }
  if (map01.isStyleLoaded()) {
    initSnapper()
  } else {
    map01.once('idle', initSnapper)
  }

  // クリックで既存ライン/ポイント/ポリゴンを選択
  map01.on('click', (e) => {
    // テキストクリックがApp.vueのラベルレイヤーで処理済みの場合はスキップ
    if (_textClickHandled) {
      _textClickHandled = false
      return
    }
    // サムネイルマーカーのクリックで処理済みの場合はスキップ
    if (_markerClickHandled) {
      _markerClickHandled = false
      return
    }

    // 描画中は何もしない（setTimeout内のハンドラで処理）
    if (store.state.isDrawPolygon || store.state.isDrawLine) {
      return
    }

    // 標高図作成モード中は選択エフェクトを表示しない
    if (store.state.elevationDrawMode) {
      return
    }

    // フリーメモモード中またはフリーメモアクティブ中は選択エフェクトを表示しない
    if (store.state.isFieldNoteMemoMode || store.state.isFieldNoteMemoActive) {
      return
    }

    const lat = e.lngLat.lat
    const lng = e.lngLat.lng
    const coordinates = [lng, lat]
    store.state.coordinates = coordinates
    const source = map01.getSource(clickCircleSource.iD)
    const geojson = getSourceData(source) || {}

    // クリック判定：既存のポリゴン/ライン/ポイントを選択
    const interactionPriority = store.state.drawInteractionPriority || 'snap'
    const allowNonPointSelection = !store.state.isDrawPoint || interactionPriority !== 'snap'
    const polygonTargetId = allowNonPointSelection ? getPolygonFeatureIdAtClick(map01, e) : null
    const lineTargetId = allowNonPointSelection ? getLineFeatureIdAtClickByPixel(map01, e) : null
    const pointTargetId = getNearbyClickCirclePointId(map01, e)

    // 優先順位：ポイント > ライン > ポリゴン
    let selectedId = null
    let selectedFeature = null
    if (pointTargetId) {
      selectedId = pointTargetId
      selectedFeature = geojson?.features?.find((f) => f.properties?.id === pointTargetId)
      if (selectedFeature && selectedFeature.geometry?.type === 'Point') {
        const p = selectedFeature.properties || {}
        // 選択した点のスタイルを次回に引き継ぐ
        // labelTypeは'0'が通常テキストなので??を使用
        const savedStyle = {
          color: p.color || store.state.currentTextColor || 'black',
          'point-color': p['point-color'] || store.state.currentPointColor || 'black',
          label: p.label || '',
          'text-size': p['text-size'] || store.state.currentTextSize || 16,
          labelType: p.labelType ?? '1',
          balloonDirection: p.balloonDirection || 'top',
        }
        store.state.lastPointStyle = savedStyle
      }
    } else if (lineTargetId) {
      selectedId = lineTargetId
      selectedFeature = geojson?.features?.find((f) => f.properties?.id === lineTargetId)
      if (selectedFeature && selectedFeature.geometry?.type === 'LineString') {
        const p = selectedFeature.properties || {}
        // フリーハンドかどうかで保存先を分ける
        if (p.keiko) {
          store.state.lastFreeHandStyle = {
            color: p.color || p.colorName || 'black',
            'keiko-color': p['keiko-color'],
            'line-width': p['line-width'] || 3,
            label: p.label || '',
            keiko: p.keiko || 0,
          }
        } else {
          store.state.lastLineStyle = {
            color: p.color || p.colorName || 'black',
            colorName: p.colorName || p.color || 'black',
            'line-width': p['line-width'] || 3,
            label: p.label || '',
            arrowType: p['arrow-type'] || 'none',
            arrowColor: p['arrow-type'] || 'none',
            labelType: p.labelType || '0',
            calc: p.calc || 0,
            keiko: p.keiko || 0,
          }
        }
      }
    } else if (polygonTargetId) {
      selectedId = polygonTargetId
      selectedFeature = geojson?.features?.find((f) => f.properties?.id === polygonTargetId)
      if (selectedFeature && selectedFeature.geometry?.type === 'Polygon') {
        const p = selectedFeature.properties || {}
        // 円かどうかで保存先を分ける
        if (p.radius !== undefined) {
          store.state.lastCircleStyle = {
            color: p.color,
            colorName: p.colorName || 'black',
            'line-width': p['line-width'] || 1,
            showRadius: p.showRadius !== false,
            label2: p.label2 || '',
            opacity: p['fill-opacity'] ?? 0.6,
          }
        } else {
          store.state.lastPolygonStyle = {
            color: p.color,
            colorName: p.colorName || 'black',
            'line-width': p['line-width'] || 1,
            label: p.label || '',
            opacity: p['fill-opacity'] ?? 0.6,
            isArea: p.isArea || false,
          }
        }
      }
    }

    // 新規ポイント描画モードの場合はここで作成する
    // 投げ縄モード、編集・移動モード、投げ縄選択済み状態、またはドラッグ中は描画をブロック
    if (
      !selectedId &&
      store.state.isDrawPoint &&
      !store.state.isDrawLasso &&
      !store.state.editEnabled &&
      !store.state.isLassoSelected &&
      !store.state._isDraggingFeature
    ) {
      const id = String(Math.floor(10000 + Math.random() * 90000))
      store.state.id = id
      const currentFeature = store.state.currentDrawFeature
      const inherited =
        currentFeature && currentFeature.geometry?.type === 'Point' ? currentFeature.properties || {} : {}
      const lastStyle = store.state.lastPointStyle || {}
      const colorName = lastStyle.color || store.state.currentTextColor || 'black'
      const defaultLabel = getNextDefaultLabel('Point')
      const properties = {
        id,
        pairId: id,
        label: defaultLabel,
        color: colorName,
        'point-color': lastStyle['point-color'] || inherited['point-color'] || store.state.currentPointColor || 'black',
        'text-size': lastStyle['text-size'] || inherited['text-size'] || store.state.currentTextSize || 16,
        // labelTypeは'0'が通常テキストなので??を使用（||だと'0'がfalsyになる）
        labelType: lastStyle.labelType ?? inherited.labelType ?? store.state.currentTextLabelType ?? '1',
        balloonDirection: lastStyle.balloonDirection || inherited.balloonDirection || 'top',
        text: defaultLabel,
        // テキスト配置: 左寄せがデフォルト
        textAnchor: lastStyle.textAnchor || inherited.textAnchor || 'left',
        textJustify: lastStyle.textJustify || inherited.textJustify || 'left',
        // オフセット: 横方向を大きめに設定
        offsetValue: lastStyle.offsetValue || inherited.offsetValue || [1.2, 0],
      }
      geojsonCreate(map01, 'Point', coordinates, properties)
      // 次回用に保存
      store.state.lastPointStyle = {
        color: properties.color,
        'point-color': properties['point-color'],
        label: properties.label,
        'text-size': properties['text-size'],
        labelType: properties.labelType,
        balloonDirection: properties.balloonDirection,
        text: properties.text,
        textAnchor: properties.textAnchor,
        textJustify: properties.textJustify,
        offsetValue: properties.offsetValue,
      }
      highlightSelectedFeature(map01, id)
      scheduleAutoOpenDrawWindow(onLineClick, e, 200)
      return
    }

    // テキスト専用モードの場合はテキストを作成してTextEditWindowを開く
    if (
      !selectedId &&
      store.state.isDrawText &&
      !store.state.isDrawLasso &&
      !store.state.editEnabled &&
      !store.state.isLassoSelected &&
      !store.state._isDraggingFeature
    ) {
      createTextFeature(coordinates)
      return
    }

    if (selectedId) {
      store.state.id = selectedId
      // サムネイルマーカー常時表示OFFの場合、クリックで表示する
      if (!store.state.thumbnailAlwaysVisible) {
        const el = document.getElementById(`pic-marker-${selectedId}`)
        if (el && el.style.display === 'none') {
          el.style.display = ''
        }
      }
      // 選択された図形を強調表示
      highlightSelectedFeature(map01, selectedId)
      // FWを開く
      if (selectedFeature) {
        if (
          interactionPriority === 'info' &&
          store.state.isDrawPoint &&
          (selectedFeature.geometry?.type === 'LineString' || selectedFeature.geometry?.type === 'Polygon')
        ) {
          const infoType = selectedFeature.geometry?.type === 'LineString' ? 'line' : 'polygon'
          showFeatureInfo(selectedFeature, infoType)
          return
        }
        // フリーメモポイントの場合はフリーメモを開く（FWは開かない）
        if (selectedFeature.properties?.isFieldNoteMemo || selectedFeature.properties?.hasMemo) {
          store.state.fieldNoteMemoPointId = selectedId
          store.state.isFieldNoteMemoActive = true
          // 地物FWを明示的に閉じる
          store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
          store.commit('setCurrentDrawFeature', null)
          return
        }
        openDrawFloatingWindow(selectedFeature)
        // 線の場合は情報ボックスも表示
        if (selectedFeature.geometry?.type === 'LineString') {
          showFeatureInfo(selectedFeature, 'line')
        }
      }
    } else {
      // 編集モード中は頂点・中点上のクリックでは編集を終了しない
      if (store.state.editEnabled && e.point) {
        const canvasPoint = e.point
        const vertexHit = map01.queryRenderedFeatures(
          [[canvasPoint.x - 15, canvasPoint.y - 15], [canvasPoint.x + 15, canvasPoint.y + 15]],
          { layers: ['vertex-layer'] }
        )
        const midpointHit = map01.queryRenderedFeatures(
          [[canvasPoint.x - 15, canvasPoint.y - 15], [canvasPoint.x + 15, canvasPoint.y + 15]],
          { layers: ['midpoint-layer'] }
        )
        if (vertexHit.length > 0 || midpointHit.length > 0) {
          // 頂点・中点上のクリックは編集モード維持
          return
        }
      }
      // 何もクリックされていない場合は選択をクリア
      clearSelectedFeature(map01)
      // 編集モードを終了（変形・移動を確定）
      if (store.state.editingFeatureId) {
        disableFeatureEdit()
      }
      // ドローFWを閉じる
      store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
      store.commit('setCurrentDrawFeature', null)
      // DrawDrawerも閉じる
      store.commit('setDrawDrawer', false)
    }
  })

  // 以下は古いtouchend実装（コメントアウト済み、参考用に保持）
  /*
    map01.on('touchend', (e) => {
        if (!store.state.isDrawPolygon && !store.state.isDrawLine) return;

        // パン（地図移動）された場合は座標を追加しない
        if (touchMoved) {
            return;
        }

        // タイムスタンプを記録（clickイベントとの重複防止）
        lastTouchEndTime = Date.now();

        const touch = e.changedTouches?.[0] || e.originalEvent?.changedTouches?.[0];
        if (!touch) return;
        const rect = map01.getCanvas().getBoundingClientRect();
        const point = {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        };
        const lngLat = map01.unproject(point);

        // 正規化
        let lng = lngLat.lng;
        lng = ((lng + 180) % 360 + 360) % 360 - 180;
        const ll = { lng, lat: lngLat.lat };
        const p = [ll.lng, ll.lat];

        // 多角形の場合、座標を追加
        if (store.state.isDrawPolygon) {
            const last = tempPolygonCoords[tempPolygonCoords.length - 1];
            if (!last || last[0] !== p[0] || last[1] !== p[1]) {
                tempPolygonCoords.push(p);
                // 座標追加後、プレビューを更新
                updateGuideLine(map01, tempPolygonCoords, ll);
            }
        }

        // Lineの場合、座標を追加
        if (store.state.isDrawLine) {
            const last = tempLineCoords[tempLineCoords.length - 1];
            if (!last || last[0] !== p[0] || last[1] !== p[1]) {
                tempLineCoords.push(p);
                // 座標追加後、プレビューを更新
                updateGuideLine(map01, tempLineCoords, ll);
            }
        }

        // ガイド始点も同じ点で
        if (map01.getSource('guide-line-source')) {
            const lastG = tempLineCoordsGuide[tempLineCoordsGuide.length - 1];
            if (!lastG || lastG[0] !== p[0] || lastG[1] !== p[1]) {
                tempLineCoordsGuide.push(p);
            }
        }
    });
    */
}
function getNearbyClickCirclePointId(map, e, pixelTolerance = 10) {
  if (!e || !e.lngLat) return null
  const clickPixel = map.project(e.lngLat)
  const source = map.getSource(clickCircleSource.iD)
  const rawData = source?._data
  const geojson = rawData && rawData.geojson ? rawData.geojson : rawData
  const features = geojson?.features || []
  for (const f of features) {
    if (!f.geometry || f.geometry.type !== 'Point') continue
    // テキストフィーチャーはスキップ（テキスト専用FWで処理）
    if (f.properties?.type === 'Text') continue
    const [lng2, lat2] = f.geometry.coordinates
    const featurePixel = map.project({ lng: lng2, lat: lat2 })
    const dx = featurePixel.x - clickPixel.x
    const dy = featurePixel.y - clickPixel.y
    const pixelDist = Math.sqrt(dx * dx + dy * dy)
    if (pixelDist < pixelTolerance) {
      return f.properties?.id ?? null // id プロパティを返す。なければ null
    }
  }
  return null
}
function getLineFeatureIdAtClickByPixel(map, e, pixelTolerance = 10) {
  if (!e || !e.lngLat) return null
  const clickPixel = map.project(e.lngLat)
  const source = map.getSource(clickCircleSource.iD)
  const rawData = source?._data
  const geojson = rawData && rawData.geojson ? rawData.geojson : rawData
  let features = geojson?.features || []
  features = features.filter((f) => f.properties.id !== 'config')
  const found = features.find((f) => {
    if (f.geometry.type !== 'LineString') return false
    const coords = f.geometry.coordinates
    // 各線分ごとに最短ピクセル距離を調べる
    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = map.project({ lng: coords[i][0], lat: coords[i][1] })
      const p2 = map.project({ lng: coords[i + 1][0], lat: coords[i + 1][1] })
      const d = pointToSegmentDistance(clickPixel, p1, p2)
      if (d <= pixelTolerance) return true
    }
    return false
  })
  return found ? found.properties.id : null
}
// 2点間の線分への最短距離（ピクセル空間で）
function pointToSegmentDistance(pt, p1, p2) {
  const x = pt.x,
    y = pt.y
  const x1 = p1.x,
    y1 = p1.y
  const x2 = p2.x,
    y2 = p2.y
  const dx = x2 - x1
  const dy = y2 - y1
  if (dx === 0 && dy === 0) {
    // 線分が点の場合
    return Math.sqrt((x - x1) ** 2 + (y - y1) ** 2)
  }
  // t: 線分上の最近点パラメータ（0～1）
  const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy)))
  const projX = x1 + t * dx
  const projY = y1 + t * dy
  return Math.sqrt((x - projX) ** 2 + (y - projY) ** 2)
}

// ポリゴンのクリック判定
function getPolygonFeatureIdAtClick(map, e) {
  if (!e || !e.lngLat) return null
  const clickPoint = turf.point([e.lngLat.lng, e.lngLat.lat])
  const source = map.getSource(clickCircleSource.iD)
  const rawData = source?._data
  const geojson = rawData && rawData.geojson ? rawData.geojson : rawData
  const features = geojson?.features || []

  // ポリゴンのみをフィルタリングし、クリック位置が内部にあるものを検索
  const found = features.find((f) => {
    if (!f.geometry || f.geometry.type !== 'Polygon') return false
    if (f.properties.id === 'config') return false
    try {
      return turf.booleanPointInPolygon(clickPoint, f)
    } catch (e) {
      return false
    }
  })

  return found ? found.properties.id : null
}

// 選択されたフィーチャを強調表示（選択レイヤーにコピー）
export function highlightSelectedFeature(map, featureId) {
  // 編集・移動モード中は選択レイヤーを表示しない
  if (store.state.editEnabled) {
    const selectionSource = map.getSource('draw-selection-source')
    if (selectionSource) {
      selectionSource.setData({ type: 'FeatureCollection', features: [] })
    }
    return
  }
  if (!featureId) return

  const source = map.getSource(clickCircleSource.iD)
  if (!source || !source._data) return

  const rawData = source._data
  const geojson = rawData && rawData.geojson ? rawData.geojson : rawData
  if (!geojson || !geojson.features) return

  const features = geojson.features
  const selectedFeature = features.find((f) => f.properties && f.properties.id === featureId)

  if (selectedFeature) {
    // 選択されたフィーチャのコピーを作成して選択レイヤーに表示
    const selectionSource = map.getSource('draw-selection-source')
    if (selectionSource) {
      selectionSource.setData({
        type: 'FeatureCollection',
        features: [selectedFeature],
      })
    }

    // 選択されたフィーチャの情報を表示
    const geomType = selectedFeature.geometry?.type
    if (geomType === 'Polygon') {
      showFeatureInfo(selectedFeature, 'polygon')
    } else if (geomType === 'LineString') {
      showFeatureInfo(selectedFeature, 'line')
    }
  }
}

// 選択をクリア
export function clearSelectedFeature(map) {
  const selectionSource = map.getSource('draw-selection-source')
  if (selectionSource) {
    selectionSource.setData({
      type: 'FeatureCollection',
      features: [],
    })
  }
  // 情報表示もクリア
  clearFeatureInfo()
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 */

// ---- ユーティリティ ----
/**
 * prevGeojsonsの使い回しが不具合を起こすかもしれない。要注意
 */
function currentRow() {
  return store.state.prevGeojsons[store.state.historyCursor] || null
}

function applySnapshot(row) {
  const map01 = store.state.map01
  store.state.isApplyingHistory = true // ①適用ガード ON
  try {
    const geojson = JSON.parse(row.geojsonString)
    map01.getSource(clickCircleSource.iD).setData(geojson)
    // ここでテキスト等の副作用がウォッチされても保存されないよう isApplyingHistory を見せる
    store.state.clickCircleGeojsonText = row.geojsonString
    generateStartEndPointsFromGeoJSON(geojson)
    generateSegmentLabelGeoJSON(geojson)
    // 確定ポリゴンの辺ラベルを更新
    updatePolygonEdgeLabels(geojson)
    if (!store.state.isUsingServerGeojson) featureCollectionAdd()
    markerAddAndRemove(true)
    store.state.updatePermalinkFire = !store.state.updatePermalinkFire
    // Undo/Redo時は選択をクリア
    clearSelectedFeature(map01)
  } finally {
    store.state.isApplyingHistory = false // ②適用ガード OFF（即時・確実）
  }
}

function pushSnapshotFromGeoJSON(geojsonObj) {
  if (store.state.isApplyingHistory) return // Undo/Redo中は積まない
  // 共有ドロー（マイドロー/サーバー保存モード）時は履歴を積まない
  if (store.state.isUsingServerGeojson) return
  const s = typeof geojsonObj === 'string' ? geojsonObj : JSON.stringify(geojsonObj)
  const stack = store.state.prevGeojsons
  const cur = currentRow()
  if (cur && cur.geojsonString === s) return // 同一は積まない
  // Undo後に編集があれば Redo枝を破棄
  if (store.state.historyCursor < stack.length - 1) {
    stack.splice(store.state.historyCursor + 1)
  }
  stack.push({ geojsonString: s, ts: Date.now() })
  const maxHistory = 200
  if (stack.length > maxHistory) {
    const overflow = stack.length - maxHistory
    stack.splice(0, overflow)
    store.state.historyCursor = Math.max(0, store.state.historyCursor - overflow)
  }
  store.state.historyCursor = stack.length - 1
}

// デバウンスで配列をつくる
export const pushSnapshotDebounced = debounce((geojsonString) => {
  if (store.state.isApplyingHistory) return
  pushSnapshotFromGeoJSON(geojsonString)
}, 400) // 間隔はお好みで

// ---- 初期保存の例（最初の状態を1発入れておくとUndoが効く） ----
export function seedInitialSnapshot(geojsonObj) {
  store.state.prevGeojsons = []
  store.state.historyCursor = -1
  pushSnapshotFromGeoJSON(geojsonObj)
}

// ---- Undo / Redo ----
// 通常ドロー用（従来の全体スナップショット方式）
function drawUndoLocal() {
  if (store.state.historyCursor <= 0) return // もう戻れない
  store.state.historyCursor -= 1
  const row = currentRow()
  if (row) applySnapshot(row)
}

function drawRedoLocal() {
  if (store.state.historyCursor >= store.state.prevGeojsons.length - 1) return // 進めない
  store.state.historyCursor += 1
  const row = currentRow()
  if (row) applySnapshot(row)
}

// 統合Undo関数（マイドローと通常ドローで分岐）
export function drawUndo() {
  // 権限チェック
  if (!store.state.isEditable && !store.state.isMine) {
    showError('編集不可です')
    return
  }

  // マイドローモードの場合はフィーチャー単位のUndo
  if (store.state.isUsingServerGeojson) {
    executeMyDrawUndo()
    return
  }

  // 通常ドローの場合は従来の全体スナップショット方式
  drawUndoLocal()
}

// 統合Redo関数（マイドローと通常ドローで分岐）
export function drawRedo() {
  // 権限チェック
  if (!store.state.isEditable && !store.state.isMine) {
    showError('編集不可です')
    return
  }

  // マイドローモードの場合はフィーチャー単位のRedo
  if (store.state.isUsingServerGeojson) {
    executeMyDrawRedo()
    return
  }

  // 通常ドローの場合は従来の全体スナップショット方式
  drawRedoLocal()
}

export function applyHistoryAt(index) {
  if (store.state.isUsingServerGeojson) return
  const stack = store.state.prevGeojsons
  if (!Array.isArray(stack) || !stack.length) return
  if (index < 0 || index >= stack.length) return
  store.state.historyCursor = index
  const row = currentRow()
  if (row) applySnapshot(row)
}

function updateGuideLine(map, baseCoords, cursorLL) {
  const gsrc = map.getSource('guide-line-source')
  if (!gsrc || !baseCoords?.length) return

  // 前回のカーソル位置と比較して、変更がない場合はスキップ
  if (
    lastGuideLineCursor &&
    almostEq(lastGuideLineCursor.lng, cursorLL.lng) &&
    almostEq(lastGuideLineCursor.lat, cursorLL.lat)
  ) {
    return
  }

  lastGuideLineCursor = { lng: cursorLL.lng, lat: cursorLL.lat }

  // 全マップ更新用の配列
  const otherMaps = ['map02', 'map03', 'map04']

  const guideCoords = baseCoords.concat([[cursorLL.lng, cursorLL.lat]])

  const features = []

  // ポリゴン描画モードの場合は仮のポリゴンを表示
  if (store.state.isDrawPolygon && baseCoords.length >= 2) {
    // 閉じたポリゴンとして表示（最初の座標を最後に追加）
    const closedCoords = [...guideCoords, guideCoords[0]]
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [closedCoords] },
      properties: { isPreview: true },
    })
  } else {
    // ライン描画モードの場合はLineStringとして表示
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: guideCoords },
      properties: {},
    })
  }

  // 各座標点を円として表示
  baseCoords.forEach((coord) => {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: coord },
      properties: {},
    })
  })

  // 全マップにガイドラインデータを設定
  const guideData = { type: 'FeatureCollection', features: features }
  gsrc.setData(guideData)
  otherMaps.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource) {
      const otherGsrc = otherMap.getSource('guide-line-source')
      if (otherGsrc) otherGsrc.setData(guideData)
    }
  })

  // 距離・角度ラベルの更新（チェックボックスがONの場合のみ）
  const labelSrc = map.getSource('guide-label-source')
  if (!labelSrc) return

  // 円描画モードの場合はラベル非表示
  if (store.state.isDrawCircle) {
    const emptyData = { type: 'FeatureCollection', features: [] }
    labelSrc.setData(emptyData)
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.getSource) {
        const otherLabelSrc = otherMap.getSource('guide-label-source')
        if (otherLabelSrc) otherLabelSrc.setData(emptyData)
      }
    })
    return
  }

  const labelFeatures = []
  const isPolygon = store.state.isDrawPolygon

  // ポリゴンの場合は閉じた座標列を使用（閉じるセグメントも含める）
  const coordsForLabels = isPolygon && guideCoords.length >= 3
    ? [...guideCoords, guideCoords[0]]
    : guideCoords

  // 距離ラベル生成（各セグメントの中点に配置）
  function kmOrm(value) {
    return value >= 1 ? `約${value.toFixed(1)}km` : `約${(value * 1000).toFixed(1)}m`
  }

  let totalDistance = 0
  const segmentCount = isPolygon ? coordsForLabels.length - 1 : guideCoords.length - 1
  for (let i = 0; i < segmentCount; i++) {
    const from = coordsForLabels[i]
    const to = coordsForLabels[i + 1]
    const segment = turf.lineString([from, to])
    const center = turf.midpoint(turf.point(from), turf.point(to))
    const dist = turf.length(segment, { units: 'kilometers' })
    totalDistance += dist
    const distance = kmOrm(dist)
    const isLast = i === segmentCount - 1
    const distanceLabel = isLast && segmentCount > 1 ? `${distance}\n(計:${kmOrm(totalDistance)})` : distance

    // ベアリング計算（テキストの回転用）
    const bearing = turf.bearing(turf.point(from), turf.point(to))
    // テキストが読みやすいように調整（90度回転して垂直方向に）
    let textBearing = bearing + 90
    if (textBearing > 90 && textBearing < 270) {
      textBearing += 180
    }

    labelFeatures.push({
      type: 'Feature',
      geometry: center.geometry,
      properties: {
        distance: distanceLabel,
        textBearing: textBearing % 360,
      },
    })
  }

  // 角度ラベル生成
  if (isPolygon && guideCoords.length >= 3) {
    // ポリゴン: 全頂点の内角を計算（閉じた形状なので全ての頂点に角度がある）
    for (let i = 0; i < guideCoords.length; i++) {
      const prevIdx = (i - 1 + guideCoords.length) % guideCoords.length
      const nextIdx = (i + 1) % guideCoords.length
      const prev = guideCoords[prevIdx]
      const curr = guideCoords[i]
      const next = guideCoords[nextIdx]

      // ベクトル計算で内角を求める
      const v1x = prev[0] - curr[0]
      const v1y = prev[1] - curr[1]
      const v2x = next[0] - curr[0]
      const v2y = next[1] - curr[1]

      // 内積とベクトルの長さから角度を計算
      const dot = v1x * v2x + v1y * v2y
      const len1 = Math.sqrt(v1x * v1x + v1y * v1y)
      const len2 = Math.sqrt(v2x * v2x + v2y * v2y)
      const cosAngle = dot / (len1 * len2)
      // 数値誤差で-1〜1を超える場合があるのでクランプ
      const clampedCos = Math.max(-1, Math.min(1, cosAngle))
      const angleRad = Math.acos(clampedCos)
      const angleDeg = Math.round(angleRad * 180 / Math.PI)

      labelFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: curr,
        },
        properties: {
          distance: `${angleDeg}°`,
          isAngle: true,
          textBearing: 0,
        },
      })
    }
  } else {
    // ライン: 最初と最後を除く頂点の角度を計算
    for (let i = 1; i < guideCoords.length - 1; i++) {
      const prev = guideCoords[i - 1]
      const curr = guideCoords[i]
      const next = guideCoords[i + 1]

      // ベクトル計算で内角を求める
      const v1x = prev[0] - curr[0]
      const v1y = prev[1] - curr[1]
      const v2x = next[0] - curr[0]
      const v2y = next[1] - curr[1]

      // 内積とベクトルの長さから角度を計算
      const dot = v1x * v2x + v1y * v2y
      const len1 = Math.sqrt(v1x * v1x + v1y * v1y)
      const len2 = Math.sqrt(v2x * v2x + v2y * v2y)
      const cosAngle = dot / (len1 * len2)
      // 数値誤差で-1〜1を超える場合があるのでクランプ
      const clampedCos = Math.max(-1, Math.min(1, cosAngle))
      const angleRad = Math.acos(clampedCos)
      const angleDeg = Math.round(angleRad * 180 / Math.PI)

      labelFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: curr,
        },
        properties: {
          distance: `${angleDeg}°`,
          isAngle: true,
          textBearing: 0,
        },
      })
    }
  }

  // 全マップにラベルデータを設定
  const labelData = { type: 'FeatureCollection', features: labelFeatures }
  labelSrc.setData(labelData)
  otherMaps.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource) {
      const otherLabelSrc = otherMap.getSource('guide-label-source')
      if (otherLabelSrc) otherLabelSrc.setData(labelData)
    }
  })
}

// クリックされたポリゴン/ラインの情報を表示（グローバル関数）
export function showFeatureInfo(feature, type) {
  // 編集・移動モード中はFWを開かない
  if (store.state.editEnabled) {
    console.info('[edit-mode] skip showFeatureInfo (editEnabled=true)', {
      type,
      featureId: feature?.properties?.id,
    })
    return
  }
  console.info('[edit-mode] showFeatureInfo state', { editEnabled: store.state.editEnabled })
  const geom = feature.geometry
  let areaM2 = 0
  let distanceM = 0

  if (type === 'polygon' && geom.type === 'Polygon') {
    const poly = turf.polygon(geom.coordinates)
    areaM2 = turf.area(poly)
  } else if (type === 'line' && geom.type === 'LineString') {
    const line = turf.lineString(geom.coordinates)
    distanceM = turf.length(line, { units: 'meters' })
  }


  // Vue インスタンスのdataを更新
  if (window.vueApp) {
    window.vueApp.clickedFeatureInfo = {
      type: type,
      area: areaM2,
      distance: distanceM,
      feature: feature,
    }
    // 情報を表示
    updateRealtimeInfoForClick(areaM2, distanceM, type, feature)
  } else {
    console.warn('❌ window.vueApp not found')
  }
}

export function clearFeatureInfo() {
  const infoElement = document.getElementById('draw-realtime-info')
  if (infoElement) {
    infoElement.innerHTML = ''
  }
  if (window.vueApp) {
    window.vueApp.clickedFeatureInfo = null
  }
  // 情報ボックス自体も非表示にする
  store.state.drawInfoVisible = false
}

function onLineClick(e) {

  // 標高図作成モード中は何もしない
  if (store.state.elevationDrawMode) {
    return
  }

  const map01 = store.state.map01

  // FeatureIDから該当のFeatureを取得
  const featureId = store.state.id
  if (!featureId) return

  // すぐにFWを開く（処理完了を待たない）
  const source = map01.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)

  // Featureがまだ存在しない場合は仮のFeatureを作成
  let feature = geojson?.features?.find((f) => f.properties?.id === featureId)

  if (!feature) {
    // 仮のFeatureを作成（後で更新される）
    feature = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [] },
      properties: { id: featureId },
    }
  }

  // すぐにFWを開く
  openDrawFloatingWindow(feature)
}

function finishPreview() {
  const emptyData = { type: 'FeatureCollection', features: [] }
  const allMaps = ['map01', 'map02', 'map03', 'map04']

  // 全マップのプレビューソースをクリア
  allMaps.forEach(mapName => {
    const map = store.state[mapName]
    if (!map || !map.getSource) return

    const guide = map.getSource('guide-line-source')
    const guideLabel = map.getSource('guide-label-source')
    const preview = map.getSource('freehand-preview-source')
    const circlePreview = map.getSource('circle-preview-source')
    const vertex = map.getSource('vertex-source')
    const midpoint = map.getSource('midpoint-source')
    const dragHandles = map.getSource('drag-handles-source')

    if (guide) guide.setData(emptyData)
    if (guideLabel) guideLabel.setData(emptyData)
    if (preview) preview.setData(emptyData)
    if (circlePreview) circlePreview.setData(emptyData)
    if (vertex) vertex.setData(emptyData)
    if (midpoint) midpoint.setData(emptyData)
    if (dragHandles) dragHandles.setData(emptyData)
  })

  // ツールチップを非表示
  hideCircleRadiusTooltip()

  isDrawingLine = false
  tempFreehandCoords.length = 0
  tempCircleCenter = null
  tempCircleRadius = null
  tempPolygonCoords.length = 0
  tempLineCoordsGuide.length = 0

  // ガイドライン最適化用の前回位置をリセット
  lastGuideLineCursor = null

  // リアルタイム情報はクリアしない（ユーザーがバツボタンで閉じるまで表示）
  // clearRealtimeInfo()
}
// 最後の頂点を一つ削除してプレビューを更新
export function removeLastVertex() {
  if (store.state.isDrawLine) {
    if (!tempLineCoords.length) return
    const coords = tempLineCoords
    if (!coords.length) return
    // // 最後の頂点がカーソル位置と重なっている場合は2回削除
    // if (this.lastMouseLngLat && coords.length >= 1) {
    //     const [lastLng, lastLat] = coords[coords.length - 1];
    //     if (lastLng === this.lastMouseLngLat.lng && lastLat === this.lastMouseLngLat.lat) {
    //         // カーソル重なり頂点を2回削除
    //         coords.pop();
    //         if (coords.length) coords.pop();
    //         this.tempLineCoordsGuide = this.tempLineCoords
    //         this.updateDynamicLinePreview();
    //         return;
    //     }
    // }
    // それ以外は1回だけ削除
    coords.pop()
    tempLineCoordsGuide = tempLineCoords
    updateDynamicLinePreview()
    // リアルタイム情報を更新
    updateRealtimeInfo(tempLineCoords, 'line')
  } else if (store.state.isDrawPolygon) {
    const coords = tempPolygonCoords
    if (!coords.length) return
    // // 最後の頂点がカーソル位置と重なっている場合は2回削除
    // if (this.lastMouseLngLat && coords.length >= 1) {
    //     const [lastLng, lastLat] = coords[coords.length - 1];
    //     if (lastLng === this.lastMouseLngLat.lng && lastLat === this.lastMouseLngLat.lat) {
    //         // カーソル重なり頂点を2回削除
    //         coords.pop();
    //         // guideCoords.pop()
    //         if (coords.length) coords.pop();
    //         this.tempLineCoordsGuide = this.tempPolygonCoords
    //         this.updateDynamicPolygonPreview();
    //         return;
    //     }
    // }
    // それ以外は1回だけ削除
    coords.pop()
    tempLineCoordsGuide = tempPolygonCoords
    updateDynamicPolygonPreview()
    // リアルタイム情報を更新
    updateRealtimeInfo(tempPolygonCoords, 'polygon')
  }
}
// 動的ラインプレビュー（全マップ対応）
function updateDynamicLinePreview() {
  const coords = tempLineCoords
  const data = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      },
    ],
  }
  const allMaps = ['map01', 'map02', 'map03', 'map04']
  allMaps.forEach(mapName => {
    const map = store.state[mapName]
    if (map && map.getSource) {
      const src = map.getSource('guide-line-source')
      if (src) src.setData(data)
    }
  })
}
// 動的ポリゴンプレビュー（全マップ対応）
function updateDynamicPolygonPreview() {
  const coords = tempPolygonCoords
  const data = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      },
    ],
  }
  const allMaps = ['map01', 'map02', 'map03', 'map04']
  allMaps.forEach(mapName => {
    const map = store.state[mapName]
    if (map && map.getSource) {
      const src = map.getSource('guide-line-source')
      if (src) src.setData(data)
    }
  })
}

export function drawConfirm() {
  let minPoints
  if (store.state.isDrawPolygon) {
    minPoints = 3
  } else if (store.state.isDrawLine) {
    minPoints = 2
  }
  tempLineCoordsGuide = dedupeCoords(tempLineCoordsGuide)
  if (tempLineCoordsGuide.length < minPoints) {
    store.state.loadingMessage3 = `ポイントが足りません。最低${minPoints}点必要です。`
    store.state.loading3 = true
    setTimeout(() => {
      store.state.loading3 = false
    }, 2000)
    return
  }
  finishDrawing()
  finishPreview()
  store.state.showDrawConfrim = false
  store.state.editEnabled = false
  // 編集結果をサーバーへ保存（移動・変形を永続化）
  persistClickCircleGeojson()
  // 確定ポリゴンの辺ラベルを更新
  const geojsonText = store.state.clickCircleGeojsonText
  updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
  // 円の縁のシアン色をリセット
  highlightCircleEdgesForEdit(false)
}

async function getIntersectsFeatures(map, layerId, polygon) {
  const features = map.queryRenderedFeatures({
    layers: [layerId],
  })
  // GeoJSON形式に変換
  const pmtilesFeatures = {
    type: 'FeatureCollection',
    features: features.map((f) => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: f.properties,
    })),
  }


  const intersectsFeatures = pmtilesFeatures.features.filter((feature) => {
    if (turf.booleanIntersects(polygon, feature)) {
      return feature
    }
  })
  return intersectsFeatures
}

// 描画完了時のアニメーション効果
function playDrawCompletionEffect(map, coords, geometryType) {
  try {

    // パルスエフェクト用の一時的なソースとレイヤーを作成
    const pulseSourceId = 'draw-pulse-source'
    const pulseFillLayerId = 'draw-pulse-fill-layer'
    const pulseLineLayerId = 'draw-pulse-line-layer'

    // 既存のパルスエフェクトをクリーンアップ
    if (map.getLayer(pulseFillLayerId)) map.removeLayer(pulseFillLayerId)
    if (map.getLayer(pulseLineLayerId)) map.removeLayer(pulseLineLayerId)
    if (map.getSource(pulseSourceId)) map.removeSource(pulseSourceId)

    // パルスエフェクト用のジオメトリを作成
    let geometry
    if (geometryType === 'Polygon') {
      geometry = { type: 'Polygon', coordinates: coords }
    } else if (geometryType === 'LineString') {
      geometry = { type: 'LineString', coordinates: coords }
    } else {
      return // サポート外のジオメトリ
    }

    // ソースを追加
    map.addSource(pulseSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: geometry,
        properties: {},
      },
    })

    // レイヤーを追加（青系のエフェクト）
    if (geometryType === 'Polygon') {
      map.addLayer({
        id: pulseFillLayerId,
        type: 'fill',
        source: pulseSourceId,
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.8,
        },
      })
    }

    map.addLayer({
      id: pulseLineLayerId,
      type: 'line',
      source: pulseSourceId,
      paint: {
        'line-color': '#2196F3',
        'line-width': 8,
        'line-opacity': 1,
      },
    })

    // アニメーション（フェードアウト）- より遅く、目立つように
    let opacity = geometryType === 'Polygon' ? 0.8 : 1
    let lineOpacity = 1
    let lineWidth = 8
    let startTime = Date.now()
    const duration = 1000 // 1秒間のアニメーション

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      opacity = (geometryType === 'Polygon' ? 0.8 : 1) * (1 - progress)
      lineOpacity = 1 - progress
      lineWidth = 8 + progress * 12 // 8px -> 20px

      if (progress < 1) {
        if (geometryType === 'Polygon' && map.getLayer(pulseFillLayerId)) {
          map.setPaintProperty(pulseFillLayerId, 'fill-opacity', opacity)
        }
        if (map.getLayer(pulseLineLayerId)) {
          map.setPaintProperty(pulseLineLayerId, 'line-opacity', lineOpacity)
          map.setPaintProperty(pulseLineLayerId, 'line-width', lineWidth)
        }
        requestAnimationFrame(animate)
      } else {
        // アニメーション完了後にクリーンアップ
        setTimeout(() => {
          if (map.getLayer(pulseFillLayerId)) map.removeLayer(pulseFillLayerId)
          if (map.getLayer(pulseLineLayerId)) map.removeLayer(pulseLineLayerId)
          if (map.getSource(pulseSourceId)) map.removeSource(pulseSourceId)
        }, 100)
      }
    }

    requestAnimationFrame(animate)
  } catch (error) {
    console.error('パルスエフェクトエラー:', error)
  }
}

function finishDrawing(createdFeature = null) {

  // フリーハンド標高図モードの場合は、既に作成済みのfeatureを使用
  if (createdFeature && (store.state.elevationDrawMode === 'line' || store.state.elevationDrawMode === 'free')) {
    store.state.tgtFeature = createdFeature
    clearSelectedFeature(store.state.map01)
    store.commit('setFloatingVisible', {
      id: 'elevation-dialog',
      visible: true,
    })
    return
  }

  // if (this.s_editEnabled) {
  //     this.toggleEditEnabled()
  // }
  const map01 = store.state.map01
  const source = map01.getSource('click-circle-source')
  const geojson = getSourceData(source)

  // ソースが空で、既存のclickCircleGeojsonTextにデータがある場合は上書きしない
  // （初期化時にソースにデータがセットされる前に呼ばれるケースを防ぐ）
  const existingText = store.state.clickCircleGeojsonText
  const hasExistingData = existingText && existingText.length > 100
  const sourceIsEmpty = !geojson?.features?.length || geojson.features.filter(f => f.properties?.id !== 'config').length === 0

  if (sourceIsEmpty && hasExistingData) {
  } else {
    store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  }

  const id = String(Math.floor(10000 + Math.random() * 90000))
  store.state.id = id
  let coords, properties
  if (store.state.isDrawPolygon) {
    if (!tempPolygonCoords || tempPolygonCoords.length < 2) return // 最低2点以上だけ確定
    coords = [tempPolygonCoords.concat([tempPolygonCoords[0]])]
    // 前回のスタイルを引き継ぐ
    const lastStyle = store.state.lastPolygonStyle || {}
    const colorName = lastStyle.colorName || store.state.currentPolygonColor || 'black'
    const alpha = lastStyle.opacity ?? 0.6
    // 面積・周長を計算（文字列フォーマット）
    const polygon = turf.polygon(coords)
    const areaM2 = turf.area(polygon)
    const perimeterM = turf.length(turf.polygonToLine(polygon), { units: 'meters' })

    properties = {
      id: id,
      pairId: id,
      label: getNextDefaultLabel('Polygon'),
      color: colorNameToRgba(colorName, alpha),
      colorName: colorName,
      'line-width': lastStyle['line-width'] || 1,
      opacity: alpha,
      isArea: store.state.currentPolygonAreaCheck || false,
      area: '約' + areaM2.toFixed(1) + 'm2',
      perimeter: '約' + perimeterM.toFixed(1) + 'm',
    }
    geojsonCreate(map01, 'Polygon', coords, properties)
    // 新規作成した地物を強調表示
    highlightSelectedFeature(map01, id)

    // パルスエフェクトを再生
    // playDrawCompletionEffect(map01, coords, 'Polygon');

    const dummyEvent = {
      lngLat: {
        lng: tempPolygonCoords[0][0],
        lat: tempPolygonCoords[0][1],
      },
    }
    store.state.coordinates = [tempPolygonCoords[0][0], tempPolygonCoords[0][1]]
    scheduleAutoOpenDrawWindow(onLineClick, dummyEvent, 500)
    tempPolygonCoords.length = 0
    // 確定ポリゴンの辺ラベルを更新（isAreaがtrueの場合のみ表示される）
    const geojsonText = store.state.clickCircleGeojsonText
    updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
  } else if (store.state.isDrawLine) {
    // tempLineCoordsGuideを使用(drawConfirmから呼ばれる場合はこちらに座標が入っている)
    const lineCoords = tempLineCoordsGuide.length > 0 ? tempLineCoordsGuide : tempLineCoords
    if (!lineCoords || lineCoords.length < 2) {
      return // 最低2点以上だけ確定
    }
    // 前回のスタイルを引き継ぐ
    const lastStyle = store.state.lastLineStyle || {}
    const colorName = lastStyle.colorName || store.state.currentLineColor || 'black'
    properties = {
      id: id,
      pairId: id,
      label: store.state.elevationDrawMode ? '' : getNextDefaultLabel('LineString'),
      color: lastStyle.color || colorNameToRgba(colorName, 1),
      colorName: colorName,
      offsetValue: [0.6, 0],
      'line-width': lastStyle['line-width'] || store.state.currentLineWidth || 5,
      'line-style': lastStyle['line-style'] || 'solid',
      textAnchor: 'left',
      textJustify: 'left',
      'arrow-type': lastStyle.arrowType || store.state.currentArrowType || 'end',
      arrowType: lastStyle.arrowType || store.state.currentArrowType || 'end', // 後方互換
      arrowColor: lastStyle.arrowColor || store.state.currentArrowColor || 'arrow_black',
      labelType: lastStyle.labelType || store.state.currentLineLabelType || 'start',
      calc: store.state.currentLineCalcCheck || 0, // localStorageから復元した値を使用
      keiko: lastStyle.keiko !== undefined ? lastStyle.keiko : 0,
    }
    const createdFeature = geojsonCreate(map01, 'LineString', lineCoords.slice(), properties)
    // 新規作成した地物を強調表示
    highlightSelectedFeature(map01, id)

    // パルスエフェクトを再生
    // playDrawCompletionEffect(map01, tempLineCoords.slice(), 'LineString');

    // 標高図作成モードの場合、専用ダイアログを開く
    if (store.state.elevationDrawMode === 'line' || store.state.elevationDrawMode === 'free') {
      // tgtFeatureに作成した線を設定
      store.state.tgtFeature = createdFeature
      // 選択エフェクトをクリア
      clearSelectedFeature(map01)
      // 標高図ダイアログを開く（設定画面が表示される）
      store.commit('setFloatingVisible', {
        id: 'elevation-dialog',
        visible: true,
      })
    } else {
      const dummyEvent = {
        lngLat: {
          lng: lineCoords[0][0],
          lat: lineCoords[0][1],
        },
      }
      store.state.coordinates = [lineCoords[0][0], lineCoords[0][1]]
      scheduleAutoOpenDrawWindow(onLineClick, dummyEvent, 500)
    }
    tempLineCoords.length = 0
    tempLineCoordsGuide.length = 0
  }
  finishPreview()
  // 新しい図形を描いたので既存の選択をクリア（情報ボックスはクリアしない）
  const selectionSource = map01.getSource('draw-selection-source')
  if (selectionSource) {
    selectionSource.setData({
      type: 'FeatureCollection',
      features: [],
    })
  }
  // 連続描画モードの場合は確定ボタンを非表示にする
  // ただし、編集モード(editEnabled)の場合は確定ボタンが必要なので除外
  if (store.state.drawContinuousMode && !store.state.editEnabled) {
    store.state.showDrawConfrim = false
  }
}

export function drawCancel() {
  const map01 = store.state.map01
  finishPreview()
  clearRealtimeInfo() // キャンセル時は情報をクリア
  clearSelectedFeature(map01) // 選択をクリア
  store.state.showDrawConfrim = false
  if (store.state.editEnabled) {
    map01.getSource('click-circle-source').setData(store.state.prevGeojson)
    finishDrawing()
    store.state.clickCircleGeojsonText = JSON.stringify(store.state.prevGeojson)
    store.state.prevGeojson = null
    store.state.editEnabled = false
    // サーバー側も編集前の状態に戻す
    persistClickCircleGeojson()
    // 確定ポリゴンの辺ラベルを更新（編集前に戻す）
    const geojsonText = store.state.clickCircleGeojsonText
    updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
  }
  // 円の縁のシアン色をリセット
  highlightCircleEdgesForEdit(false)
}

// =============================================================
// Drawing Mode Toggle Functions (moved from App.vue)
// =============================================================

/**
 * Toggle drawing mode on/off
 */
export function toggleDrawMode() {
  const map01 = store.state.map01

  // 🔍 デバッグログ: toggleDrawMode開始時
  const debugSource = map01?.getSource('click-circle-source')
  if (debugSource) {
    const rawData = debugSource._data
    const geojson = rawData?.geojson || rawData
  }

  // Close lasso control window
  store.commit('setFloatingVisible', { id: 'lasso-control-window', visible: false })

  if (!store.state.isDraw) {
    store.commit('disableAllDraws')
    if (store.state.originalDragPanEnable) {
      map01.dragPan.enable = store.state.originalDragPanEnable
      map01.dragPan.enable()
    }
    store.state.isDrawFix = false
    // Close all popups
    const popups = document.querySelectorAll('.maplibregl-popup')
    popups.forEach((popup) => popup.remove())
  }

  finishDrawing()
}

/**
 * Toggle draw fix mode (lock map panning)
 */
export function toggleDrawFix(originalEnable) {
  const map = store.state.map01
  store.state.isDrawFix = !store.state.isDrawFix

  if (store.state.isDrawFix) {
    map.dragPan.disable()
    finishDrawing()

    const originalEnableFn = map.dragPan.enable
    map.dragPan.enable = function (...args) {
      console.trace('dragPan.enable() called')
      setTimeout(() => {
        map.dragPan.disable()
      }, 50)
      return originalEnableFn.apply(this, args)
    }
  } else {
    if (originalEnable) {
      map.dragPan.enable = originalEnable
      map.dragPan.enable()
    }
  }
  store.state.isCursorOnPanel = false
}

// click-circle-source の現在内容を state とサーバーに反映
function persistClickCircleGeojson() {
  try {
    const map01 = store.state.map01
    const source = map01?.getSource('click-circle-source')
    const geojson = source ? getSourceData(source) : null
    if (!geojson || !geojson.features) return

    // 編集・移動モード中は確定ボタンまでサーバー保存しない
    if (store.state.editEnabled) {
      console.info('[edit-mode] persistClickCircleGeojson skipped (editEnabled=true)')
      return
    }

    store.state.clickCircleGeojsonText = JSON.stringify(geojson)
    featureCollectionAdd()
    markerAddAndRemove(true)
    saveDrowFeatures(geojson.features)
  } catch (e) {
    console.warn('[persistClickCircleGeojson] failed:', e)
  }
}

/**
 * 編集モード時に円の縁を強調表示
 * @param {boolean} enabled - 編集モードが有効かどうか
 */
function highlightCircleEdgesForEdit(enabled) {
  const map01 = store.state.map01
  if (!map01) return

  const layerId = 'click-circle-polygon-line-layer'
  if (!map01.getLayer(layerId)) return

  if (enabled) {
    // 編集モード時：円の縁を太く、シアン色に
    // 円（radiusプロパティを持つPolygon）のみ強調
    map01.setPaintProperty(layerId, 'line-width', [
      'case',
      ['has', 'radius'],
      5, // 円は太く
      ['has', 'line-width'],
      ['get', 'line-width'],
      1, // デフォルト値
    ])
    map01.setPaintProperty(layerId, 'line-color', [
      'case',
      ['has', 'radius'],
      '#00BCD4', // 円はシアン色
      ['has', 'lineColor'],
      ['get', 'lineColor'],
      'black',
    ])
  } else {
    // 通常モード：元に戻す（円も黒に戻す）
    map01.setPaintProperty(layerId, 'line-width', [
      'case',
      ['has', 'radius'],
      1, // 円は元の太さ（1）に戻す
      ['has', 'line-width'],
      ['get', 'line-width'],
      1, // デフォルト値
    ])
    map01.setPaintProperty(layerId, 'line-color', [
      'case',
      ['has', 'radius'],
      'black', // 円は黒に戻す
      ['has', 'lineColor'],
      ['get', 'lineColor'],
      'black',
    ])
  }
}

function toggleDrawModeState(modeKey, { indicatorText = '', finish = 'drawing' } = {}) {
  const map01 = store.state.map01
  clearSelectedFeature(map01)

  const enabling = !store.state[modeKey]
  if (enabling) {
    store.commit('SET_DRAW_MODE', modeKey)
  } else {
    store.commit('disableAllDraws')
  }

  store.state.editEnabled = false

  // 投げ縄モード終了時のみFWを閉じる（開始時は表示しない、選択完了時に表示）
  if (modeKey === 'isDrawLasso' && !enabling) {
    store.commit('setFloatingVisible', { id: 'lasso-control-window', visible: false })
  }

  const indicator = document.querySelector('#draw-indicato-text')
  if (indicator) indicator.innerHTML = indicatorText

  if (finish === 'preview') {
    finishPreview()
  } else {
    finishDrawing()
  }
}

/**
 * Toggle edit mode (move/transform features)
 */
export function toggleEditEnabled() {
  const map01 = store.state.map01
  clearSelectedFeature(map01) // 選択をクリア

  console.info('[edit-mode] toggleEditEnabled called. before:', store.state.editEnabled)
  store.state.editEnabled = !store.state.editEnabled
  console.info('[edit-mode] toggleEditEnabled after:', store.state.editEnabled)
  // 編集モード中は各地物FWを開かない
  if (store.state.editEnabled) {
    store.commit('setCurrentDrawFeature', null)
    store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
  }

  // 円の縁を強調表示（編集モード時のみ）
  highlightCircleEdgesForEdit(store.state.editEnabled)

  if (store.state.editEnabled) {
    store.commit('disableAllDraws')
    store.state.showDrawConfrim = true

    if (map01 && map01.getSource('click-circle-source')) {
      const source = map01.getSource('click-circle-source')
      const geojson = getSourceData(source)
      store.state.prevGeojson = JSON.parse(JSON.stringify(geojson))
    }
  } else {
    store.state.showDrawConfrim = false
    // 編集完了時にポリゴンの辺ラベルを更新
    const geojsonText = store.state.clickCircleGeojsonText
    updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)
  }

  finishDrawing()
  // Remove markers
  const markers = document.querySelectorAll('.marker-vertex, .marker-midpoint')
  markers.forEach((marker) => marker.remove())
  // Close all popups
  const popups = document.querySelectorAll('.maplibregl-popup')
  popups.forEach((popup) => popup.remove())
}

// =============================================================
// 個別地物編集用の移動ハンドル
// =============================================================
const FEATURE_EDIT_MOVE_SOURCE = 'feature-edit-move-source'
const FEATURE_EDIT_MOVE_LAYER = 'feature-edit-move-layer'

/**
 * 個別地物編集用の移動ハンドルレイヤーを作成
 */
async function createFeatureEditMoveLayer(map) {
  // 移動ハンドル用アイコンを登録（各ステップを独立してチェック）
  const MOVE_HANDLE_ICON = 'oh-lasso-move-handle'
  if (!map.hasImage(MOVE_HANDLE_ICON)) {
    await registerMdiIcon(map, {
      name: MOVE_HANDLE_ICON,
      path: mdiCursorMove,
      size: 48,
      color: '#3265BA',
      padding: 4,
      strokeWidth: 2,
      strokeColor: '#ffffff',
    })
  }

  // ソースが存在しない場合のみ作成
  if (!map.getSource(FEATURE_EDIT_MOVE_SOURCE)) {
    map.addSource(FEATURE_EDIT_MOVE_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    })
  }

  // レイヤーが存在しない場合のみ作成
  if (!map.getLayer(FEATURE_EDIT_MOVE_LAYER)) {
    map.addLayer({
      id: FEATURE_EDIT_MOVE_LAYER,
      type: 'symbol',
      source: FEATURE_EDIT_MOVE_SOURCE,
      layout: {
        'icon-image': MOVE_HANDLE_ICON,
        'icon-allow-overlap': true,
        'icon-anchor': 'center',
      }
    })
  }

  // 移動ハンドルを最前面に移動
  if (map.getLayer(FEATURE_EDIT_MOVE_LAYER)) {
    map.moveLayer(FEATURE_EDIT_MOVE_LAYER)
  }
}

/**
 * 個別地物編集用の移動ハンドルを表示（全マップ）
 */
async function showFeatureEditMoveHandle(featureId) {
  const map01 = store.state.map01
  if (!map01) return

  await createFeatureEditMoveLayer(map01)

  // 対象地物を取得
  const source = map01.getSource('click-circle-source')
  if (!source) return
  const geojson = getSourceData(source)
  const feature = geojson?.features?.find(f => f.properties?.id === featureId)
  if (!feature) return

  // 地物の中心点を計算
  let centroid
  try {
    centroid = turf.centroid(feature)
  } catch (e) {
    console.warn('[showFeatureEditMoveHandle] centroid計算失敗:', e)
    return
  }

  // ラインの場合、中点マーカーと重ならないよう上方向にオフセット
  const geomType = feature.geometry?.type
  if (geomType === 'LineString' || geomType === 'MultiLineString') {
    const bbox = turf.bbox(feature)
    const height = bbox[3] - bbox[1] // maxY - minY
    centroid.geometry.coordinates[1] += height * 0.3 // 高さの30%上にずらす
  }

  // 移動ハンドルデータ
  const moveHandleData = {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: centroid.geometry,
      properties: { type: 'move', featureId }
    }]
  }

  // map01に移動ハンドルを表示
  const moveSource = map01.getSource(FEATURE_EDIT_MOVE_SOURCE)
  if (moveSource) {
    moveSource.setData(moveHandleData)
  }

  // 移動ハンドルを最前面に移動
  if (map01.getLayer(FEATURE_EDIT_MOVE_LAYER)) {
    map01.moveLayer(FEATURE_EDIT_MOVE_LAYER)
  }

  // map02, map03, map04にも移動ハンドルを表示
  const otherMaps = ['map02', 'map03', 'map04']
  for (const mapName of otherMaps) {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource) {
      await createFeatureEditMoveLayer(otherMap)
      const otherMoveSource = otherMap.getSource(FEATURE_EDIT_MOVE_SOURCE)
      if (otherMoveSource) {
        otherMoveSource.setData(moveHandleData)
      }
      if (otherMap.getLayer(FEATURE_EDIT_MOVE_LAYER)) {
        otherMap.moveLayer(FEATURE_EDIT_MOVE_LAYER)
      }
    }
  }

  // ドラッグイベントを設定（map01のみ - プロキシで転送されるため）
  setupFeatureEditMoveEvents(map01, featureId)
}

/**
 * 個別地物編集用の移動ハンドルを非表示（全マップ）
 */
function hideFeatureEditMoveHandle() {
  const map01 = store.state.map01
  const emptyData = { type: 'FeatureCollection', features: [] }

  // map01の移動ハンドルを非表示
  if (map01) {
    const source = map01.getSource(FEATURE_EDIT_MOVE_SOURCE)
    if (source) {
      source.setData(emptyData)
    }
    // ドラッグイベントを解除
    cleanupFeatureEditMoveEvents(map01)
  }

  // map02, map03, map04の移動ハンドルも非表示
  const otherMaps = ['map02', 'map03', 'map04']
  otherMaps.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource) {
      const source = otherMap.getSource(FEATURE_EDIT_MOVE_SOURCE)
      if (source) {
        source.setData(emptyData)
      }
    }
  })
}

// ドラッグ状態管理
let featureEditMoveState = {
  isDragging: false,
  featureId: null,
  startLngLat: null,
  handlers: null
}

/**
 * 移動ハンドルのドラッグイベントを設定
 */
function setupFeatureEditMoveEvents(map, featureId) {
  // 既存のハンドラがあれば解除
  cleanupFeatureEditMoveEvents(map)

  const onMouseDown = (e) => {
    if (!store.state.editingFeatureId) return
    e.preventDefault()
    featureEditMoveState.isDragging = true
    featureEditMoveState.featureId = featureId
    featureEditMoveState.startLngLat = e.lngLat
    map.getCanvas().style.cursor = 'grabbing'
    map.dragPan.disable()
  }

  const onMouseMove = (e) => {
    if (!featureEditMoveState.isDragging) return
    const dx = e.lngLat.lng - featureEditMoveState.startLngLat.lng
    const dy = e.lngLat.lat - featureEditMoveState.startLngLat.lat
    moveFeatureBy(featureEditMoveState.featureId, dx, dy)
    featureEditMoveState.startLngLat = e.lngLat
    // 移動ハンドルの位置も更新
    updateFeatureEditMoveHandlePosition(featureEditMoveState.featureId)
  }

  const onMouseUp = () => {
    if (!featureEditMoveState.isDragging) return
    featureEditMoveState.isDragging = false
    map.getCanvas().style.cursor = ''
    map.dragPan.enable()
    // 他のマップのdragPanも再有効化
    const otherMaps = ['map02', 'map03', 'map04']
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.dragPan) {
        otherMap.dragPan.enable()
      }
    })
    // 保存
    saveDrowFeatures()
  }

  map.on('mousedown', FEATURE_EDIT_MOVE_LAYER, onMouseDown)
  map.on('mousemove', onMouseMove)
  map.on('mouseup', onMouseUp)

  // map02からのプロキシイベント用ハンドラ
  const onProxyMouseDown = (e) => {
    if (!e._fromMap02Proxy) return
    if (!store.state.editingFeatureId) return

    const canvasPoint = e.point
    if (!canvasPoint) return

    // 移動ハンドルレイヤーのfeaturesを検索
    const features = map.queryRenderedFeatures(
      [[canvasPoint.x - 20, canvasPoint.y - 20], [canvasPoint.x + 20, canvasPoint.y + 20]],
      { layers: [FEATURE_EDIT_MOVE_LAYER] }
    )

    if (features.length > 0) {
      // map02のdragPanも無効化
      const otherMaps = ['map02', 'map03', 'map04']
      otherMaps.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.dragPan) {
          otherMap.dragPan.disable()
        }
      })
      onMouseDown(e)
    }
  }
  map.on('mousedown', onProxyMouseDown)

  // タッチ対応
  map.on('touchstart', FEATURE_EDIT_MOVE_LAYER, (e) => {
    if (!store.state.editingFeatureId) return
    e.preventDefault()
    featureEditMoveState.isDragging = true
    featureEditMoveState.featureId = featureId
    featureEditMoveState.startLngLat = e.lngLat
    map.dragPan.disable()
  })
  map.on('touchmove', (e) => {
    if (!featureEditMoveState.isDragging) return
    const touch = e.originalEvent.touches[0]
    const lngLat = map.unproject([touch.clientX, touch.clientY])
    const dx = lngLat.lng - featureEditMoveState.startLngLat.lng
    const dy = lngLat.lat - featureEditMoveState.startLngLat.lat
    moveFeatureBy(featureEditMoveState.featureId, dx, dy)
    featureEditMoveState.startLngLat = lngLat
    updateFeatureEditMoveHandlePosition(featureEditMoveState.featureId)
  })
  map.on('touchend', onMouseUp)

  // カーソル
  map.on('mouseenter', FEATURE_EDIT_MOVE_LAYER, () => {
    if (!featureEditMoveState.isDragging) {
      map.getCanvas().style.cursor = 'grab'
    }
  })
  map.on('mouseleave', FEATURE_EDIT_MOVE_LAYER, () => {
    if (!featureEditMoveState.isDragging) {
      map.getCanvas().style.cursor = ''
    }
  })

  featureEditMoveState.handlers = { onMouseDown, onMouseMove, onMouseUp, onProxyMouseDown }
}

/**
 * 移動ハンドルのドラッグイベントを解除
 */
function cleanupFeatureEditMoveEvents(map) {
  if (!map) return

  // 保存されたハンドラーがあれば解除
  if (featureEditMoveState.handlers) {
    const { onMouseDown, onMouseMove, onMouseUp, onProxyMouseDown } = featureEditMoveState.handlers
    if (onMouseDown) map.off('mousedown', FEATURE_EDIT_MOVE_LAYER, onMouseDown)
    if (onMouseMove) map.off('mousemove', onMouseMove)
    if (onMouseUp) map.off('mouseup', onMouseUp)
    if (onProxyMouseDown) map.off('mousedown', onProxyMouseDown)
  }

  featureEditMoveState.isDragging = false
  featureEditMoveState.featureId = null
  featureEditMoveState.handlers = null
}

/**
 * 地物を指定した差分だけ移動
 */
function moveFeatureBy(featureId, dx, dy) {
  const map = store.state.map01
  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson?.features) return

  // ★重要: 最初にディープコピーを作成（MapLibreの内部データを直接変更しないため）
  const updatedGeojson = JSON.parse(JSON.stringify(geojson))

  const feature = updatedGeojson.features.find(f => f.properties?.id === featureId)
  if (!feature) return

  // 座標を移動（ディープコピー上で変更）
  // ★重要: 座標が文字列の場合があるので数値に変換してから演算
  const geometry = feature.geometry
  if (geometry.type === 'Point') {
    geometry.coordinates[0] = Number(geometry.coordinates[0]) + dx
    geometry.coordinates[1] = Number(geometry.coordinates[1]) + dy
  } else if (geometry.type === 'LineString') {
    geometry.coordinates.forEach(coord => {
      coord[0] = Number(coord[0]) + dx
      coord[1] = Number(coord[1]) + dy
    })
  } else if (geometry.type === 'Polygon') {
    geometry.coordinates.forEach(ring => {
      ring.forEach(coord => {
        coord[0] = Number(coord[0]) + dx
        coord[1] = Number(coord[1]) + dy
      })
    })
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(poly => {
      poly.forEach(ring => {
        ring.forEach(coord => {
          coord[0] = Number(coord[0]) + dx
          coord[1] = Number(coord[1]) + dy
        })
      })
    })
  }

  // ソースを更新（新しいオブジェクトを渡す）
  source.setData(updatedGeojson)
  store.state.clickCircleGeojsonText = JSON.stringify(updatedGeojson)

  // 選択ハイライト（draw-selection-source）も更新
  const updatedFeature = updatedGeojson.features.find(f => f.properties?.id === featureId)
  const selectionSource = map.getSource('draw-selection-source')
  if (selectionSource && updatedFeature) {
    selectionSource.setData({
      type: 'FeatureCollection',
      features: [updatedFeature],
    })
  }

  // 頂点・中点マーカーも更新（全マップ）
  getAllVertexPoints(map, updatedGeojson)
  setAllMidpoints(map, updatedGeojson)
  const otherMapsForMove = ['map02', 'map03', 'map04']
  otherMapsForMove.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
      otherMap.getSource('click-circle-source').setData(updatedGeojson)
      getAllVertexPoints(otherMap, updatedGeojson)
      setAllMidpoints(otherMap, updatedGeojson)
      // 選択ハイライトも更新
      const otherSelectionSource = otherMap.getSource('draw-selection-source')
      if (otherSelectionSource && updatedFeature) {
        otherSelectionSource.setData({
          type: 'FeatureCollection',
          features: [updatedFeature],
        })
      }
    }
  })

  // ライン始終点マーカー（矢印等）も更新
  generateStartEndPointsFromGeoJSON(updatedGeojson)

  // スタンプマーカーの位置も更新（アイコン化された点の場合）
  if (geometry.type === 'Point' && feature.properties?.stamp) {
    const stampMarker = store.state.stampMarkers?.[featureId]
    if (stampMarker) {
      stampMarker.setLngLat([geometry.coordinates[0], geometry.coordinates[1]])
    }
  }
}

/**
 * 移動ハンドルの位置を更新
 */
function updateFeatureEditMoveHandlePosition(featureId) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return
  const geojson = getSourceData(source)
  const feature = geojson?.features?.find(f => f.properties?.id === featureId)
  if (!feature) return

  let centroid
  try {
    centroid = turf.centroid(feature)
  } catch (e) {
    return
  }

  // ラインの場合、中点マーカーと重ならないよう上方向にオフセット
  const geomType = feature.geometry?.type
  if (geomType === 'LineString' || geomType === 'MultiLineString') {
    const bbox = turf.bbox(feature)
    const height = bbox[3] - bbox[1] // maxY - minY
    centroid.geometry.coordinates[1] += height * 0.3 // 高さの30%上にずらす
  }

  const moveHandleData = {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: centroid.geometry,
      properties: { type: 'move', featureId }
    }]
  }

  // map01の移動ハンドルを更新
  const moveSource = map.getSource(FEATURE_EDIT_MOVE_SOURCE)
  if (moveSource) {
    moveSource.setData(moveHandleData)
  }

  // map02, map03, map04の移動ハンドルも更新
  const otherMapsForHandle = ['map02', 'map03', 'map04']
  otherMapsForHandle.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource) {
      const otherMoveSource = otherMap.getSource(FEATURE_EDIT_MOVE_SOURCE)
      if (otherMoveSource) {
        otherMoveSource.setData(moveHandleData)
      }
    }
  })
}

/**
 * 特定の地物の編集モードを開始（地物単位の変形・移動）
 * @param {string} featureId - 編集する地物のID
 */
export function enableFeatureEdit(featureId) {
  const map01 = store.state.map01
  if (!map01) return

  // 既に同じ地物を編集中なら何もしない
  if (store.state.editingFeatureId === featureId) return

  // 以前の編集があれば終了
  if (store.state.editingFeatureId) {
    disableFeatureEdit()
  }

  console.info('[edit-mode] enableFeatureEdit:', featureId)

  // 編集状態を設定
  store.commit('setEditingFeatureId', featureId)

  // 描画モードを無効化
  store.commit('disableAllDraws')

  // 確定ボタン表示
  store.state.showDrawConfrim = true

  // 編集前の状態を保存
  if (map01.getSource('click-circle-source')) {
    const source = map01.getSource('click-circle-source')
    const geojson = getSourceData(source)
    store.state.prevGeojson = JSON.parse(JSON.stringify(geojson))
  }

  // ポイントの場合はマーカーを削除（移動中にずれるため）
  // 確定後に markerAddAndRemove(true) で再作成される
  const source = map01.getSource('click-circle-source')
  const geojson = getSourceData(source)
  const feature = geojson?.features?.find(f => f.properties?.id === featureId)
  console.info('[edit-mode] feature found:', !!feature, 'type:', feature?.geometry?.type)
  if (feature?.geometry?.type === 'Point') {
    console.info('[edit-mode] removing all markers for Point edit')
    removeAllMarkers()
    // 全てのmaplibreマーカーのpointer-eventsを無効化（移動ハンドルへのイベントをブロックしないように）
    document.querySelectorAll('.maplibregl-marker').forEach(el => {
      el.style.pointerEvents = 'none'
    })
    console.info('[edit-mode] disabled pointer-events for all markers')
  }

  // 対象地物のハイライト
  highlightFeatureForEdit(featureId, true)

  // 移動ハンドルを表示
  showFeatureEditMoveHandle(featureId)

  // 頂点・中点マーカーを表示（編集中の地物のみ）- 全マップに表示
  if (geojson) {
    getAllVertexPoints(map01, geojson)
    setAllMidpoints(map01, geojson)
    // map02, map03, map04にも頂点・中点を表示
    const otherMaps = ['map02', 'map03', 'map04']
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.getSource && otherMap.getSource('vertex-source')) {
        getAllVertexPoints(otherMap, geojson)
        setAllMidpoints(otherMap, geojson)
      }
    })
  }
}

/**
 * 地物の編集モードを終了
 */
export function disableFeatureEdit() {
  const map01 = store.state.map01
  const featureId = store.state.editingFeatureId

  if (!featureId) return

  console.info('[edit-mode] disableFeatureEdit:', featureId)

  // photomemoの座標更新（編集前と座標が変わっていたらDBに保存）
  const source = map01?.getSource('click-circle-source')
  if (source) {
    const geojson = getSourceData(source)
    const feature = geojson?.features?.find(f => f.properties?.id === featureId)
    const photoMemoId = feature?.properties?.photoMemoId
    if (photoMemoId && feature?.geometry?.type === 'Point') {
      const [lng, lat] = feature.geometry.coordinates
      // 編集前の座標と比較
      const prevFeature = store.state.prevGeojson?.features?.find(f => f.properties?.id === featureId)
      const [prevLng, prevLat] = prevFeature?.geometry?.coordinates || [null, null]
      if (lng !== prevLng || lat !== prevLat) {
        console.info('[edit-mode] photomemo座標更新:', { photoMemoId, lng, lat, prevLng, prevLat })
        // バックグラウンドでDB更新
        updatePhotoMemo(photoMemoId, store.state.userId, { lng, lat }).catch(err => {
          console.error('[edit-mode] photomemo座標更新失敗:', err)
        })
      }
    }
  }

  // ハイライト解除
  highlightFeatureForEdit(featureId, false)

  // 移動ハンドルを非表示
  hideFeatureEditMoveHandle()

  // 確定ボタン非表示
  store.state.showDrawConfrim = false

  // 編集状態をクリア
  store.commit('setEditingFeatureId', null)

  // 頂点・中点マーカーをクリア - 全マップ
  if (map01) {
    getAllVertexPoints(map01, null)  // 空にする
    setAllMidpoints(map01, null)     // 空にする
  }
  // map02, map03, map04もクリア
  const otherMaps = ['map02', 'map03', 'map04']
  otherMaps.forEach(mapName => {
    const otherMap = store.state[mapName]
    if (otherMap && otherMap.getSource && otherMap.getSource('vertex-source')) {
      getAllVertexPoints(otherMap, null)
      setAllMidpoints(otherMap, null)
    }
  })

  // マーカーを削除
  const markers = document.querySelectorAll('.marker-vertex, .marker-midpoint')
  markers.forEach((marker) => marker.remove())

  // enableFeatureEditで無効化したpointer-eventsを復元
  document.querySelectorAll('.maplibregl-marker').forEach(el => {
    el.style.pointerEvents = ''
  })
  console.info('[edit-mode] restored pointer-events for all markers')

  // ポップアップを閉じる
  const popups = document.querySelectorAll('.maplibregl-popup')
  popups.forEach((popup) => popup.remove())

  // ポリゴンの辺ラベルを更新
  const geojsonText = store.state.clickCircleGeojsonText
  updatePolygonEdgeLabels(geojsonText ? JSON.parse(geojsonText) : null)

  // featureCollectionを現在の座標で同期してからマーカーを再作成
  syncFeatureCollection()
  markerAddAndRemove(true)

  // 変形・移動結果をサーバーへ保存（マイドロー対応）
  const srcForSave = map01?.getSource('click-circle-source')
  if (srcForSave) {
    const geoForSave = getSourceData(srcForSave)
    if (geoForSave?.features) {
      store.state.clickCircleGeojsonText = JSON.stringify(geoForSave)
      saveDrowFeatures(geoForSave.features)
    }
  }
}

/**
 * 特定の地物をハイライト表示（編集中表示）- 全マップ
 * @param {string} featureId - 地物ID
 * @param {boolean} enabled - ハイライトON/OFF
 */
function highlightFeatureForEdit(featureId, enabled) {
  const layerId = 'click-circle-polygon-line-layer'

  // ハイライト用のペイントプロパティ
  const highlightLineWidth = [
    'case',
    ['==', ['get', 'id'], featureId],
    ['case', ['has', 'radius'], 5, ['coalesce', ['get', 'line-width'], 1]],
    ['coalesce', ['get', 'line-width'], 1],
  ]
  const highlightLineColor = [
    'case',
    ['==', ['get', 'id'], featureId],
    '#00BCD4',  // シアン色でハイライト
    ['coalesce', ['get', 'lineColor'], 'black'],
  ]

  // 通常時のペイントプロパティ
  const normalLineWidth = [
    'case',
    ['has', 'line-width'],
    ['get', 'line-width'],
    1,
  ]
  const normalLineColor = [
    'case',
    ['has', 'lineColor'],
    ['get', 'lineColor'],
    'black',
  ]

  // 全マップに適用
  const allMaps = ['map01', 'map02', 'map03', 'map04']
  allMaps.forEach(mapName => {
    const map = store.state[mapName]
    if (!map || !map.getLayer || !map.getLayer(layerId)) return

    if (enabled) {
      map.setPaintProperty(layerId, 'line-width', highlightLineWidth)
      map.setPaintProperty(layerId, 'line-color', highlightLineColor)
    } else {
      map.setPaintProperty(layerId, 'line-width', normalLineWidth)
      map.setPaintProperty(layerId, 'line-color', normalLineColor)
    }
  })
}

/**
 * Toggle lasso selection mode
 */
export function toggleDrawLasso() {
  toggleDrawModeState('isDrawLasso', { indicatorText: 'LASSO' })
}

/**
 * Execute lasso selection with a polygon
 * LassoCanvas.vueから呼び出される
 * @param {Object} polygon - Turf.js polygon
 * @param {string} mapName - マップ名（'map01'など）
 */
export async function executeLassoSelection(polygon, mapName = 'map01') {
  const map = store.state[mapName]
  if (!map) return

  if (store.state.isDraw) {
    const source = map.getSource(clickCircleSource.iD)
    const geojson = getSourceData(source)
    if (!geojson || !geojson.features) return

    // 投げ縄選択前のgeojsonをバックアップ（キャンセル用）
    store.state.lassoBackupGeojson = JSON.stringify(geojson)
    let isLassoSelected = false
    const [pMinX, pMinY, pMaxX, pMaxY] = turf.bbox(polygon)

    // 1. まず投げ縄と交差する地物を選択
    geojson.features.forEach((feature) => {
      feature.properties.lassoSelected = false
      if (feature.geometry) {
        const [fMinX, fMinY, fMaxX, fMaxY] = turf.bbox(feature)
        if (fMaxX < pMinX || fMinX > pMaxX || fMaxY < pMinY || fMinY > pMaxY) return
        if (turf.booleanIntersects(feature, polygon)) {
          feature.properties.lassoSelected = true
          isLassoSelected = true
        }
      }
    })

    // 2. 選択された地物のペア（円の中心点、線の矢印など）も選択する
    const selectedIds = new Set()
    geojson.features.forEach((feature) => {
      if (feature.properties.lassoSelected) {
        selectedIds.add(feature.properties.id)
      }
    })
    geojson.features.forEach((feature) => {
      if (feature.properties.lassoSelected) return // 既に選択済み
      const id = feature.properties.id
      const pairId = feature.properties.pairId
      // 円の中心点（{parentId}-point形式）
      if (id && id.endsWith('-point')) {
        const parentId = id.replace(/-point$/, '')
        if (selectedIds.has(parentId)) {
          feature.properties.lassoSelected = true
        }
      }
      // 線の矢印など（pairIdで紐付け）
      if (pairId && selectedIds.has(pairId)) {
        feature.properties.lassoSelected = true
      }
    })

    store.state.lassoGeojson = JSON.stringify(
      turf.featureCollection(geojson.features.filter((f) => f.properties.lassoSelected === true))
    )
    store.state.isLassoSelected = !!isLassoSelected
    source.setData(geojson)
    store.state.clickCircleGeojsonText = JSON.stringify(geojson)
    clickCircleSource.obj.data = geojson
    await saveDrowFeatures(geojson.features)

    // Transform Boxを表示
    if (isLassoSelected) {
      await showTransformBox()
    }
  } else if (store.state.isDrawLassoForTokizyo) {
    pushSelectionUndo(store)
    store.state.lastHighlightedChiban = null
    const layerId = 'oh-homusyo-2025-polygon'
    const intersectsFeatures = await getIntersectsFeatures(map, layerId, polygon)
    store.state.highlightedChibans = new Set()
    intersectsFeatures.forEach((f) => {
      const targetId = `${f.properties['筆ID']}_${f.properties['地図名']}`
      store.state.highlightedChibans.add(targetId)
    })
    highlightSpecificFeatures2025(map, layerId)
    // 投げ縄完了時に右ドロワーを開く
    if (intersectsFeatures.length > 0) {
      store.state.popupFeatureProperties = intersectsFeatures[0].properties
      store.state.rightDrawerTitle = '2025登記所地図'
      store.commit('setRightDrawer', true)
    }
  } else if (store.state.isDrawLassoForTokizyo2026) {
    pushSelectionUndo(store)
    store.state.lastHighlightedChiban = null
    const layerId2026 = 'oh-homusyo-2025-fill'
    const intersectsFeatures2026 = await getIntersectsFeatures(map, layerId2026, polygon)
    // 既存の2026選択(_tokizyoサフィックス)をクリアして新規セット
    const newSet = new Set()
    store.state.highlightedFeatures = store.state.highlightedFeatures || new Map()
    store.state.highlightedFeatures.forEach((_, k) => {
      if (!k.endsWith('_tokizyo')) newSet.add(k)
    })
    const newFeatures = new Map()
    store.state.highlightedFeatures.forEach((v, k) => {
      if (!k.endsWith('_tokizyo')) newFeatures.set(k, v)
    })
    intersectsFeatures2026.forEach((f) => {
      const targetId = `${f.properties['地図番号']}_${f.properties['筆ID']}_${f.properties['地番']}_tokizyo`
      newSet.add(targetId)
      newFeatures.set(targetId, {
        type: 'Feature',
        geometry: f.geometry,
        properties: { ...f.properties, _targetId: targetId }
      })
    })
    store.state.highlightedChibans = newSet
    store.state.highlightedFeatures = newFeatures
    // fill-color を直接更新
    if (map.getLayer(layerId2026)) {
      const selectedKeys = Array.from(newSet).filter(k => k.endsWith('_tokizyo'))
      const concatExpr = ['concat', ['get', '地図番号'], '_', ['get', '筆ID'], '_', ['get', '地番'], '_tokizyo']
      const colorExpr = selectedKeys.length > 0
        ? ['case', ['in', concatExpr, ['literal', selectedKeys]], 'rgba(60, 120, 220, 0.45)', 'rgba(0, 0, 0, 0)']
        : 'rgba(0, 0, 0, 0)'
      map.setPaintProperty(layerId2026, 'fill-color', colorExpr)
    }
    // 投げ縄完了時に右ドロワーを開く
    if (intersectsFeatures2026.length > 0) {
      store.state.popupFeatureProperties = intersectsFeatures2026[0].properties
      store.state.rightDrawerTitle = '2026登記所地図テスト'
      store.commit('setRightDrawer', true)
    }
  } else if (store.state.isDrawLassoForChibanzu) {
    const layers = layersOnScreen(map)
    const layerId = layers.filter((layer) => layer.id.includes('oh-chiban'))[0]?.id
    if (layerId) {
      pushSelectionUndo(store)
      const intersectsFeatures = await getIntersectsFeatures(map, layerId, polygon)
      store.state.highlightedChibans = new Set()
      intersectsFeatures.forEach((f) => {
        // Shozai + Chiban がある場合はそれを優先（浜松市FGB対応）
        let targetId
        if (f.properties['Shozai'] !== undefined && f.properties['Chiban'] !== undefined) {
          targetId = `${f.properties['Shozai']}_${f.properties['Chiban']}`
        } else if (layerId.includes('oh-chibanzu')) {
          targetId = `${f.properties['id']}`
        } else {
          targetId = `${f.properties['oh3id']}`
        }
        store.state.highlightedChibans.add(targetId)
      })
      highlightSpecificFeaturesCity(map, layerId)
      // 投げ縄完了時に右ドロワーを開く（地番図）
      if (intersectsFeatures.length > 0) {
        store.state.popupFeatureProperties = intersectsFeatures[0].properties
        store.state.rightDrawerTitle = '地番図'
        store.commit('setRightDrawer', true)
      }
    }
  }
}

/**
 * Toggle freehand drawing mode
 */
export function toggleDrawFree() {
  toggleDrawModeState('isDrawFree', { indicatorText: 'FREE' })
}

/**
 * Toggle polygon drawing mode
 */
export function toggleDrawPolygon() {
  toggleDrawModeState('isDrawPolygon', { indicatorText: 'POLYGON' })
}

/**
 * Toggle line drawing mode
 */
export function toggleDrawLine() {
  toggleDrawModeState('isDrawLine', { indicatorText: 'LINE' })
}

/**
 * Toggle circle drawing mode
 */
export function toggleDrawCircle() {
  toggleDrawModeState('isDrawCircle', { indicatorText: 'CIRCLE' })
}

/**
 * Toggle point drawing mode
 */
export function toggleDrawPoint() {
  toggleDrawModeState('isDrawPoint', { indicatorText: 'TXT', finish: 'preview' })
}

/**
 * Toggle text drawing mode (テキスト専用モード)
 */
export function toggleDrawText() {
  const indicatorText = store.state.isDrawText ? '' : 'ABC'
  toggleDrawModeState('isDrawText', { indicatorText, finish: 'preview' })
}

/**
 * テキストを作成する
 * @param {Array} coordinates - [lng, lat]
 * @param {string} color - テキスト色
 * @param {number} fontSize - フォントサイズ
 */
export function createTextFeature(coordinates, color, fontSize) {
  const map01 = store.state.map01
  const id = 'text-' + Date.now()

  // 前回の設定を復元（lastTextStyleから取得）
  const lastStyle = store.state.lastTextStyle || {}
  const savedColor = lastStyle.color || color || 'red' // デフォルト赤
  const savedFontSize = lastStyle['text-size'] || fontSize || 30 // デフォルト30
  const savedTextAnchor = lastStyle.textAnchor || 'left'
  const savedLabel = lastStyle.label || ''

  const properties = {
    id,
    type: 'Text',
    label: savedLabel, // 前回のラベルを引き継ぎ
    text: savedLabel,
    color: savedColor,
    'text-size': savedFontSize,
    textAnchor: savedTextAnchor,
    textJustify: savedTextAnchor, // textAnchorと同じ値
    labelType: '0', // テキストのみ表示
    balloonDirection: 'top',
    'point-color': 'rgba(0,0,0,0)', // ポイントを透明に
  }

  geojsonCreate(map01, 'Point', coordinates, properties)
  highlightSelectedFeature(map01, id)

  // 作成したフィーチャーを取得してDrawFloatingWindowを開く
  const source = map01.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)
  const feature = geojson?.features?.find((f) => f.properties?.id === id)
  if (feature) {
    openDrawFloatingWindow(feature)
  }
}

// =============================================================
// リアルタイム面積・距離表示機能
// =============================================================

/**
 * 面積を計算して複数の単位で返す
 * @param {Array} coordinates - ポリゴンの座標配列 [[lng, lat], ...]
 * @returns {Object} 面積情報 {m2, tsubo, ha, km2}
 */
function calculateArea(coordinates) {
  if (!coordinates || coordinates.length < 3) {
    return { m2: 0, tsubo: 0, ha: 0, km2: 0 }
  }

  try {
    // 閉じた座標配列を作成
    const closedCoords = [...coordinates]
    if (JSON.stringify(closedCoords[0]) !== JSON.stringify(closedCoords[closedCoords.length - 1])) {
      closedCoords.push(closedCoords[0])
    }

    const polygon = turf.polygon([closedCoords])
    const area = turf.area(polygon) // 平方メートル

    return {
      m2: area,
      tsubo: area / 3.30579, // 坪
      ha: area / 10000, // ヘクタール
      km2: area / 1000000, // 平方キロメートル
    }
  } catch (e) {
    console.error('Area calculation error:', e)
    return { m2: 0, tsubo: 0, ha: 0, km2: 0 }
  }
}

/**
 * 距離を計算して複数の単位で返す
 * @param {Array} coordinates - ラインの座標配列 [[lng, lat], ...]
 * @returns {Object} 距離情報 {m, km}
 */
function calculateLength(coordinates) {
  if (!coordinates || coordinates.length < 2) {
    return { m: 0, km: 0 }
  }

  try {
    const line = turf.lineString(coordinates)
    const length = turf.length(line, { units: 'meters' }) // メートル

    return {
      m: length,
      km: length / 1000,
    }
  } catch (e) {
    console.error('Length calculation error:', e)
    return { m: 0, km: 0 }
  }
}

/**
 * 各セグメント間の距離を計算
 */
function calculateSegmentDistances(coordinates) {
  if (!coordinates || coordinates.length < 2) {
    return []
  }

  const segments = []
  try {
    for (let i = 0; i < coordinates.length - 1; i++) {
      const from = turf.point(coordinates[i])
      const to = turf.point(coordinates[i + 1])
      const distance = turf.distance(from, to, { units: 'meters' })
      segments.push({
        index: i + 1,
        m: distance,
        km: distance / 1000,
      })
    }
  } catch (e) {
    console.error('Segment distance calculation error:', e)
  }
  return segments
}

/**
 * 確定ポリゴンの辺ラベル（距離・内角）を更新
 * isArea=true のポリゴンのみ対象
 * ラインのgenerateSegmentLabelGeoJSONと同じパターン
 */
export function updatePolygonEdgeLabels(geojson) {
  const map01 = store.state.map01
  // map01またはソースが存在しない場合は早期リターン
  if (!map01 || !map01.getSource('polygon-edge-label-source')) {
    return
  }
  const labelFeatures = []
  if (!geojson || !geojson.features) {
    map01.getSource('polygon-edge-label-source').setData({
      type: 'FeatureCollection',
      features: [],
    })
    return
  }

  // isArea=true のポリゴンを抽出
  const areaPolygons = geojson.features.filter(
    (f) => f.geometry?.type === 'Polygon' && f.properties?.isArea === true
  )

  // 距離フォーマット関数
  function kmOrm(valueKm) {
    return valueKm >= 1 ? `約${valueKm.toFixed(1)}km` : `約${(valueKm * 1000).toFixed(1)}m`
  }

  for (const polygon of areaPolygons) {
    const coords = polygon.geometry.coordinates[0] // 外周のみ
    if (!coords || coords.length < 3) continue

    // 座標が閉じている場合は最後の重複点を除去
    const vertices = coords[0][0] === coords[coords.length - 1][0] &&
                     coords[0][1] === coords[coords.length - 1][1]
      ? coords.slice(0, -1)
      : coords

    if (vertices.length < 3) continue

    // ポリゴンの重心を計算
    const centroid = turf.centroid(polygon)
    const centroidCoords = centroid.geometry.coordinates

    // 各辺の距離ラベル
    for (let i = 0; i < vertices.length; i++) {
      const from = vertices[i]
      const to = vertices[(i + 1) % vertices.length]
      const segment = turf.lineString([from, to])
      const center = turf.midpoint(turf.point(from), turf.point(to))
      const dist = turf.length(segment, { units: 'kilometers' })
      const centerCoords = center.geometry.coordinates

      // 辺の中点から重心への方向を計算し、逆方向（外側）にオフセット
      const dx = centerCoords[0] - centroidCoords[0]
      const dy = centerCoords[1] - centroidCoords[1]
      const len = Math.sqrt(dx * dx + dy * dy)
      // 外側方向にオフセット
      const offsetDist = 0.00025
      const offsetX = len > 0 ? (dx / len) * offsetDist : 0
      const offsetY = len > 0 ? (dy / len) * offsetDist : 0
      const labelCoords = [centerCoords[0] + offsetX, centerCoords[1] + offsetY]

      // ベアリング計算
      const bearing = turf.bearing(turf.point(from), turf.point(to))
      let textBearing = bearing + 90
      if (textBearing > 90 && textBearing < 270) {
        textBearing += 180
      }

      labelFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: labelCoords,
        },
        properties: {
          label: kmOrm(dist),
          textBearing: textBearing % 360,
          isAngle: false,
          polygonId: polygon.properties.id,
        },
      })
    }

    // 各頂点の内角ラベル
    for (let i = 0; i < vertices.length; i++) {
      const prevIdx = (i - 1 + vertices.length) % vertices.length
      const nextIdx = (i + 1) % vertices.length
      const prev = vertices[prevIdx]
      const curr = vertices[i]
      const next = vertices[nextIdx]

      // ベクトル計算で内角を求める
      const v1x = prev[0] - curr[0]
      const v1y = prev[1] - curr[1]
      const v2x = next[0] - curr[0]
      const v2y = next[1] - curr[1]

      const dot = v1x * v2x + v1y * v2y
      const len1 = Math.sqrt(v1x * v1x + v1y * v1y)
      const len2 = Math.sqrt(v2x * v2x + v2y * v2y)
      const cosAngle = dot / (len1 * len2)
      const clampedCos = Math.max(-1, Math.min(1, cosAngle))
      const angleRad = Math.acos(clampedCos)
      const angleDeg = Math.round(angleRad * 180 / Math.PI)

      labelFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: curr,
        },
        properties: {
          label: `${angleDeg}°`,
          textBearing: 0,
          isAngle: true,
          polygonId: polygon.properties.id,
        },
      })
    }
  }

  map01.getSource('polygon-edge-label-source').setData({
    type: 'FeatureCollection',
    features: labelFeatures,
  })
}

/**
 * 数値を見やすくフォーマット
 */
function formatNumber(num, decimals = 2) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(decimals) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(decimals) + 'K'
  } else if (num >= 100) {
    return num.toFixed(0)
  } else if (num >= 10) {
    return num.toFixed(1)
  } else {
    return num.toFixed(decimals)
  }
}

/**
 * 円半径ツールチップを表示
 * @param {MouseEvent|TouchEvent} event - マウスまたはタッチイベント
 * @param {number} radius - 半径（メートル）
 */
function showCircleRadiusTooltip(event, radius) {
  const tooltip = document.getElementById('circle-radius-tooltip')
  if (!tooltip) return

  // タッチイベントの場合はタッチ座標を使用
  let clientX, clientY
  if (event.touches && event.touches.length > 0) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }

  tooltip.textContent = `半径 ${radius}m`
  tooltip.style.left = `${clientX}px`
  tooltip.style.top = `${clientY}px`
  tooltip.style.display = 'block'
}

/**
 * 円半径ツールチップを非表示
 */
function hideCircleRadiusTooltip() {
  const tooltip = document.getElementById('circle-radius-tooltip')
  if (tooltip) {
    tooltip.style.display = 'none'
  }
}

/**
 * リアルタイム情報を更新
 * @param {Array} coordinates - 座標配列
 * @param {string} type - 'polygon' or 'line' or 'circle'
 * @param {number} radiusM - 円の半径（メートル）、円の場合のみ使用
 */
export function updateRealtimeInfo(coordinates, type, radiusM = null) {
  const infoElement = document.getElementById('draw-realtime-info')
  if (!infoElement) return

  if (!coordinates || coordinates.length === 0) {
    infoElement.innerHTML = ''
    store.state.drawInfoVisible = false
    return
  }

  // 情報ボックスを表示状態にする
  store.state.drawInfoVisible = true

  let html = ''

  if (type === 'circle') {
    // 円の場合
    const radius = radiusM || 0
    const area = Math.PI * radius * radius // m²
    const circumference = 2 * Math.PI * radius // m

    html = `
            <div class="info-title">円</div>
            <div class="info-value">${formatNumber(radius, 0)} m</div>
            <div class="info-subtitle">半径</div>
            <div class="info-details">
                <div class="info-item">
                    <span class="info-label">面積</span>
                    <span class="info-number">${formatNumber(area)} m²</span>
                </div>
                <div class="info-item">
                    <span class="info-label">円周</span>
                    <span class="info-number">${formatNumber(circumference)} m</span>
                </div>
            </div>
        `
  } else if (type === 'polygon') {
    const area = calculateArea(coordinates)
    const length = calculateLength(coordinates)

    // 適切な単位を選択
    let mainArea, mainUnit
    if (area.km2 >= 1) {
      mainArea = area.km2
      mainUnit = 'km²'
    } else if (area.ha >= 0.1) {
      mainArea = area.ha
      mainUnit = 'ha'
    } else {
      mainArea = area.m2
      mainUnit = 'm²'
    }

    html = `
            <div class="info-title">多角形</div>
            <div class="info-value">${formatNumber(mainArea)} ${mainUnit}</div>
            <div class="info-secondary">
                <div class="info-item">
                    <span class="info-label">面積</span>
                    <span class="info-number">${formatNumber(area.m2)} m²</span>
                </div>
                <div class="info-item">
                    <span class="info-label">坪</span>
                    <span class="info-number">${formatNumber(area.tsubo)} 坪</span>
                </div>
                <div class="info-item">
                    <span class="info-label">周囲長</span>
                    <span class="info-number">${formatNumber(length.m)} m</span>
                </div>
                <div class="info-item">
                    <span class="info-label">頂点数</span>
                    <span class="info-number">${coordinates.length - 1}</span>
                </div>
            </div>
        `
  } else if (type === 'line') {
    const length = calculateLength(coordinates)
    const segments = calculateSegmentDistances(coordinates)

    let mainLength, mainUnit
    if (length.km >= 1) {
      mainLength = length.km
      mainUnit = 'km'
    } else {
      mainLength = length.m
      mainUnit = 'm'
    }

    // セグメント距離のHTML
    let segmentsHtml = ''
    if (segments.length > 0) {
      segmentsHtml = `
                <div class="info-segments">
                    <div class="info-label">各区間</div>
                    ${segments
                      .map((seg) => {
                        const segLength = seg.m >= 1000 ? formatNumber(seg.km) + ' km' : formatNumber(seg.m) + ' m'
                        return `<div class="info-segment-item">${seg.index}: ${segLength}</div>`
                      })
                      .join('')}
                </div>
            `
    }

    html = `
            <div class="info-item">
                <span class="info-label">距離</span>
                <span class="info-number">${formatNumber(mainLength)} ${mainUnit}</span>
            </div>
            ${segmentsHtml}
        `
  }

  infoElement.innerHTML = html
}

/**
 * リアルタイム情報をクリア
 */
export function clearRealtimeInfo() {
  const infoElement = document.getElementById('draw-realtime-info')
  if (infoElement) {
    infoElement.innerHTML = ''
  }
  store.state.drawInfoVisible = false
}

/**
 * クリックされたポリゴン/ラインの情報を表示
 */
function updateRealtimeInfoForClick(areaM2, distanceM, type, feature) {
  const infoElement = document.getElementById('draw-realtime-info')
  if (!infoElement) return

  // 情報ボックスを表示状態にする
  store.state.drawInfoVisible = true

  let html = ''

  if (type === 'polygon') {
    // 面積計算
    const area = {
      m2: areaM2,
      ha: areaM2 / 10000,
      km2: areaM2 / 1000000,
      tsubo: areaM2 / 3.30579,
    }

    // 周囲長計算
    const coords = feature.geometry.coordinates[0]
    const length = calculateLength(coords)

    // 適切な単位を選択
    let mainArea, mainUnit
    if (area.km2 >= 1) {
      mainArea = area.km2
      mainUnit = 'km²'
    } else if (area.ha >= 0.1) {
      mainArea = area.ha
      mainUnit = 'ha'
    } else {
      mainArea = area.m2
      mainUnit = 'm²'
    }

    html = `
            <div class="info-title">多角形</div>
            <div class="info-value">${formatNumber(mainArea)} ${mainUnit}</div>
            <div class="info-secondary">
                <div class="info-item">
                    <span class="info-label">面積</span>
                    <span class="info-number">${formatNumber(area.m2)} m²</span>
                </div>
                <div class="info-item">
                    <span class="info-label">坪</span>
                    <span class="info-number">${formatNumber(area.tsubo)} 坪</span>
                </div>
                <div class="info-item">
                    <span class="info-label">周囲長</span>
                    <span class="info-number">${formatNumber(length.m)} m</span>
                </div>
                <div class="info-item">
                    <span class="info-label">頂点数</span>
                    <span class="info-number">${coords.length - 1}</span>
                </div>
            </div>
        `
  } else if (type === 'line') {
    const length = {
      m: distanceM,
      km: distanceM / 1000,
    }

    let mainLength, mainUnit
    if (length.km >= 1) {
      mainLength = length.km
      mainUnit = 'km'
    } else {
      mainLength = length.m
      mainUnit = 'm'
    }

    const coords = feature.geometry.coordinates
    const segments = calculateSegmentDistances(coords)

    // セグメント距離のHTML
    let segmentsHtml = ''
    if (segments.length > 0) {
      segmentsHtml = `
                <div class="info-segments">
                    <div class="info-label">各区間</div>
                    ${segments
                      .map((seg) => {
                        const segLength = seg.m >= 1000 ? formatNumber(seg.km) + ' km' : formatNumber(seg.m) + ' m'
                        return `<div class="info-segment-item">${seg.index}: ${segLength}</div>`
                      })
                      .join('')}
                </div>
            `
    }

    html = `
            <div class="info-item">
                <span class="info-label">距離</span>
                <span class="info-number">${formatNumber(mainLength)} ${mainUnit}</span>
            </div>
            ${segmentsHtml}
        `
  }

  infoElement.innerHTML = html
}

// =============================================================
// Transform Box（投げ縄選択後のバウンディングボックス変形）
// =============================================================

const TRANSFORM_BOX_SOURCE = 'lasso-transform-box-source'
const TRANSFORM_BOX_LAYER = 'lasso-transform-box-layer'
const TRANSFORM_HANDLES_LAYER = 'lasso-transform-handles-layer'
const TRANSFORM_ROTATION_LAYER = 'lasso-transform-rotation-layer'
const TRANSFORM_ROTATION_LINE_LAYER = 'lasso-transform-rotation-line-layer'
const TRANSFORM_MOVE_LAYER = 'lasso-transform-move-layer'

// ハンドルタイプ定義
const HANDLE_TYPES = {
  CORNER_NW: 'corner-nw',
  CORNER_NE: 'corner-ne',
  CORNER_SE: 'corner-se',
  CORNER_SW: 'corner-sw',
  ROTATION: 'rotation',
  MOVE: 'move',
}

// Transform Box状態管理
let transformBoxState = {
  isActive: false,
  isDragging: false,
  dragHandle: null,
  originalBbox: null,
  originalCentroid: null,
  originalFeatures: null,
  startPoint: null,
  currentRotation: 0,
  currentScale: 1,
}

/**
 * 投げ縄選択された地物のバウンディングボックスを計算
 */
function calculateSelectedBbox() {
  const map = store.state.map01
  if (!map) return null

  const source = map.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)
  if (!geojson?.features) return null

  const selected = geojson.features.filter(f => f.properties.lassoSelected === true)
  if (selected.length === 0) return null

  const fc = turf.featureCollection(selected)
  const bbox = turf.bbox(fc)
  const centroid = turf.centroid(fc).geometry.coordinates

  return { bbox, centroid, features: selected }
}

// 移動ハンドル用アイコン名
const MOVE_HANDLE_ICON = 'oh-lasso-move-handle'

/**
 * Transform Boxのソースとレイヤーを作成
 */
export async function createTransformBoxLayers(map) {
  if (map.getSource(TRANSFORM_BOX_SOURCE)) return

  // 移動ハンドル用アイコンを登録（mdi-cursor-move）
  if (!map.hasImage(MOVE_HANDLE_ICON)) {
    await registerMdiIcon(map, {
      name: MOVE_HANDLE_ICON,
      path: mdiCursorMove,
      size: 48,
      color: '#3265BA',
      padding: 4,
      strokeWidth: 2,
      strokeColor: '#ffffff',
    })
  }

  map.addSource(TRANSFORM_BOX_SOURCE, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] }
  })

  // バウンディングボックス（矩形の線）
  map.addLayer({
    id: TRANSFORM_BOX_LAYER,
    type: 'line',
    source: TRANSFORM_BOX_SOURCE,
    filter: ['==', ['get', 'type'], 'bbox'],
    paint: {
      'line-color': '#3265BA',
      'line-width': 2,
      'line-dasharray': [4, 2],
    }
  })

  // コーナーハンドル（四隅の正方形）
  map.addLayer({
    id: TRANSFORM_HANDLES_LAYER,
    type: 'circle',
    source: TRANSFORM_BOX_SOURCE,
    filter: ['==', ['get', 'type'], 'handle'],
    paint: {
      'circle-radius': 6,
      'circle-color': '#ffffff',
      'circle-stroke-color': '#3265BA',
      'circle-stroke-width': 2,
    }
  })

  // 回転ハンドルへの線
  map.addLayer({
    id: TRANSFORM_ROTATION_LINE_LAYER,
    type: 'line',
    source: TRANSFORM_BOX_SOURCE,
    filter: ['==', ['get', 'type'], 'rotation-line'],
    paint: {
      'line-color': '#3265BA',
      'line-width': 1,
    }
  })

  // 回転ハンドル（上部の円）
  map.addLayer({
    id: TRANSFORM_ROTATION_LAYER,
    type: 'circle',
    source: TRANSFORM_BOX_SOURCE,
    filter: ['==', ['get', 'type'], 'rotation'],
    paint: {
      'circle-radius': 8,
      'circle-color': '#3265BA',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    }
  })

  // 移動ハンドル（中央のカーソル移動アイコン）
  map.addLayer({
    id: TRANSFORM_MOVE_LAYER,
    type: 'symbol',
    source: TRANSFORM_BOX_SOURCE,
    filter: ['==', ['get', 'type'], 'move'],
    layout: {
      'icon-image': MOVE_HANDLE_ICON,
      'icon-allow-overlap': true,
      'icon-anchor': 'center',
      'symbol-z-order': 'source',
    }
  })

  // 移動ハンドルを最前面に移動
  map.moveLayer(TRANSFORM_MOVE_LAYER)
}

/**
 * Transform Boxを表示
 */
export async function showTransformBox() {
  const map = store.state.map01
  if (!map) return

  const result = calculateSelectedBbox()
  if (!result) return

  const { bbox, centroid, features } = result

  // オリジナル状態を保存
  transformBoxState.originalBbox = bbox
  transformBoxState.originalCentroid = centroid
  transformBoxState.originalFeatures = JSON.parse(JSON.stringify(features))
  transformBoxState.isActive = true
  transformBoxState.currentRotation = 0
  transformBoxState.currentScale = 1

  // 投げ縄選択中はマーカーを削除（移動中にずれるため）
  // 確定後に markerAddAndRemove(true) で再作成される
  removeAllMarkers()

  await updateTransformBoxDisplay(bbox, centroid, 0)
  setupTransformBoxEvents(map)

  // BBOXのスクリーン座標を計算してFW位置を決定
  const [minX, minY, maxX, maxY] = bbox
  const bboxTopRight = map.project([maxX, maxY])
  const bboxTopLeft = map.project([minX, maxY])
  const mapContainer = map.getContainer()
  const containerWidth = mapContainer.clientWidth
  const fwWidth = 340 // FloatingWindowの幅
  const fwHeight = 320 // FloatingWindowの高さ
  const margin = 15

  let fwLeft, fwTop

  // 右側にスペースがあるか確認
  if (bboxTopRight.x + margin + fwWidth < containerWidth) {
    // 右側に配置
    fwLeft = bboxTopRight.x + margin
  } else {
    // 左側に配置
    fwLeft = Math.max(margin, bboxTopLeft.x - fwWidth - margin)
  }

  // 上端をBBOXの上端に合わせる（画面外にはみ出さないよう調整）
  fwTop = Math.max(margin, Math.min(bboxTopRight.y, mapContainer.clientHeight - fwHeight - margin))

  store.state.lassoFwPosition = { left: fwLeft, top: fwTop }

  // 選択完了後にFloatingWindowを表示
  store.commit('setFloatingVisible', { id: 'lasso-control-window', visible: true })
}

/**
 * Transform Boxの位置を更新（元に戻す時用）
 * 選択中の地物の現在の位置に合わせてTransform Boxを再配置
 * @param {Object} geojson - オプション: 直接geojsonを渡す場合（ソースから読む代わりに）
 */
export async function refreshTransformBox(geojson = null) {
  const map = store.state.map01
  if (!map) {
    return
  }

  let selected, bbox, centroid

  if (geojson && geojson.features) {
    // 直接渡されたgeojsonから計算
    selected = geojson.features.filter(f => f.properties.lassoSelected === true)
    if (selected.length === 0) {
      return
    }
    const fc = turf.featureCollection(selected)
    bbox = turf.bbox(fc)
    centroid = turf.centroid(fc).geometry.coordinates
  } else {
    // ソースから読む
    const result = calculateSelectedBbox()
    if (!result) {
      return
    }
    bbox = result.bbox
    centroid = result.centroid
    selected = result.features
  }

  // オリジナル状態を更新（元に戻した状態が新しい基準になる）
  transformBoxState.originalBbox = bbox
  transformBoxState.originalCentroid = centroid
  transformBoxState.originalFeatures = JSON.parse(JSON.stringify(selected))
  transformBoxState.currentRotation = 0
  transformBoxState.currentScale = 1

  // Transform Boxの表示を更新
  await updateTransformBoxDisplay(bbox, centroid, 0)
}

/**
 * Transform Boxの表示を更新
 */
async function updateTransformBoxDisplay(bbox, centroid, rotation = 0) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource(TRANSFORM_BOX_SOURCE)
  if (!source) {
    await createTransformBoxLayers(map)
  }

  const [minX, minY, maxX, maxY] = bbox
  const height = maxY - minY

  // 回転を考慮した座標計算
  const corners = [
    [minX, maxY], // NW
    [maxX, maxY], // NE
    [maxX, minY], // SE
    [minX, minY], // SW
  ]

  // 回転適用
  const rotatedCorners = corners.map(([x, y]) => {
    return rotatePoint([x, y], centroid, rotation)
  })

  // 回転ハンドルの位置（上部中央から上に）
  const topMidX = (rotatedCorners[0][0] + rotatedCorners[1][0]) / 2
  const topMidY = (rotatedCorners[0][1] + rotatedCorners[1][1]) / 2
  const rotationHandleDistance = height * 0.15 // bboxの高さの15%
  const rotationHandle = rotatePoint(
    [(minX + maxX) / 2, maxY + rotationHandleDistance],
    centroid,
    rotation
  )

  const features = [
    // バウンディングボックス（閉じたポリゴン）
    {
      type: 'Feature',
      properties: { type: 'bbox' },
      geometry: {
        type: 'LineString',
        coordinates: [...rotatedCorners, rotatedCorners[0]]
      }
    },
    // コーナーハンドル
    {
      type: 'Feature',
      properties: { type: 'handle', handleType: HANDLE_TYPES.CORNER_NW },
      geometry: { type: 'Point', coordinates: rotatedCorners[0] }
    },
    {
      type: 'Feature',
      properties: { type: 'handle', handleType: HANDLE_TYPES.CORNER_NE },
      geometry: { type: 'Point', coordinates: rotatedCorners[1] }
    },
    {
      type: 'Feature',
      properties: { type: 'handle', handleType: HANDLE_TYPES.CORNER_SE },
      geometry: { type: 'Point', coordinates: rotatedCorners[2] }
    },
    {
      type: 'Feature',
      properties: { type: 'handle', handleType: HANDLE_TYPES.CORNER_SW },
      geometry: { type: 'Point', coordinates: rotatedCorners[3] }
    },
    // 回転ハンドルへの線
    {
      type: 'Feature',
      properties: { type: 'rotation-line' },
      geometry: {
        type: 'LineString',
        coordinates: [[topMidX, topMidY], rotationHandle]
      }
    },
    // 回転ハンドル
    {
      type: 'Feature',
      properties: { type: 'rotation', handleType: HANDLE_TYPES.ROTATION },
      geometry: { type: 'Point', coordinates: rotationHandle }
    },
    // 移動ハンドル（中央）
    {
      type: 'Feature',
      properties: { type: 'move', handleType: HANDLE_TYPES.MOVE },
      geometry: { type: 'Point', coordinates: centroid }
    }
  ]

  const src = map.getSource(TRANSFORM_BOX_SOURCE)
  if (src) {
    src.setData({ type: 'FeatureCollection', features })
  } else {
  }
}

/**
 * 点を中心点周りに回転
 */
function rotatePoint(point, center, angleDeg) {
  const angleRad = (angleDeg * Math.PI) / 180
  const cos = Math.cos(angleRad)
  const sin = Math.sin(angleRad)
  const dx = point[0] - center[0]
  const dy = point[1] - center[1]
  return [
    center[0] + dx * cos - dy * sin,
    center[1] + dx * sin + dy * cos
  ]
}

/**
 * Transform Boxのイベントハンドラをセットアップ
 */
function setupTransformBoxEvents(map) {
  const canvas = map.getCanvas()

  // ハンドルのhover時にカーソル変更
  map.on('mouseenter', TRANSFORM_HANDLES_LAYER, () => {
    canvas.style.cursor = 'nwse-resize'
  })
  map.on('mouseleave', TRANSFORM_HANDLES_LAYER, () => {
    if (!transformBoxState.isDragging) {
      canvas.style.cursor = ''
    }
  })
  map.on('mouseenter', TRANSFORM_ROTATION_LAYER, () => {
    canvas.style.cursor = 'grab'
  })
  map.on('mouseleave', TRANSFORM_ROTATION_LAYER, () => {
    if (!transformBoxState.isDragging) {
      canvas.style.cursor = ''
    }
  })
  map.on('mouseenter', TRANSFORM_MOVE_LAYER, () => {
    canvas.style.cursor = 'move'
  })
  map.on('mouseleave', TRANSFORM_MOVE_LAYER, () => {
    if (!transformBoxState.isDragging) {
      canvas.style.cursor = ''
    }
  })

  // mousedownでドラッグ開始
  const handleMouseDown = (e) => {
    if (!transformBoxState.isActive) return

    // ハンドルがクリックされたかチェック
    const handleFeatures = map.queryRenderedFeatures(e.point, {
      layers: [TRANSFORM_HANDLES_LAYER, TRANSFORM_ROTATION_LAYER, TRANSFORM_MOVE_LAYER]
    })

    if (handleFeatures.length > 0) {
      const handle = handleFeatures[0]
      transformBoxState.isDragging = true
      transformBoxState.dragHandle = handle.properties.handleType
      transformBoxState.startPoint = [e.lngLat.lng, e.lngLat.lat]
      map.dragPan.disable()
      if (handle.properties.handleType === HANDLE_TYPES.ROTATION) {
        canvas.style.cursor = 'grabbing'
      } else if (handle.properties.handleType === HANDLE_TYPES.MOVE) {
        canvas.style.cursor = 'move'
      } else {
        canvas.style.cursor = 'nwse-resize'
      }
      e.preventDefault()
    }
  }

  // mousemoveで変形
  const handleMouseMove = (e) => {
    if (!transformBoxState.isDragging || !transformBoxState.dragHandle) return

    const currentPoint = [e.lngLat.lng, e.lngLat.lat]
    const centroid = transformBoxState.originalCentroid

    if (transformBoxState.dragHandle === HANDLE_TYPES.MOVE) {
      // 移動
      const dx = currentPoint[0] - transformBoxState.startPoint[0]
      const dy = currentPoint[1] - transformBoxState.startPoint[1]
      applyMove(dx, dy)
    } else if (transformBoxState.dragHandle === HANDLE_TYPES.ROTATION) {
      // 回転
      const startAngle = Math.atan2(
        transformBoxState.startPoint[1] - centroid[1],
        transformBoxState.startPoint[0] - centroid[0]
      )
      const currentAngle = Math.atan2(
        currentPoint[1] - centroid[1],
        currentPoint[0] - centroid[0]
      )
      const rotation = ((currentAngle - startAngle) * 180) / Math.PI
      transformBoxState.currentRotation = rotation
      applyTransform(transformBoxState.currentScale, rotation)
    } else {
      // 拡大縮小（対角コーナーからの距離比率）
      const currentDist = Math.sqrt(
        Math.pow(currentPoint[0] - centroid[0], 2) +
        Math.pow(currentPoint[1] - centroid[1], 2)
      )
      const startDist = Math.sqrt(
        Math.pow(transformBoxState.startPoint[0] - centroid[0], 2) +
        Math.pow(transformBoxState.startPoint[1] - centroid[1], 2)
      )
      const scale = currentDist / startDist
      transformBoxState.currentScale = scale
      applyTransform(scale, transformBoxState.currentRotation)
    }
  }

  // mouseupでドラッグ終了
  const handleMouseUp = () => {
    if (transformBoxState.isDragging) {
      transformBoxState.isDragging = false
      transformBoxState.dragHandle = null
      map.dragPan.enable()
      canvas.style.cursor = ''

      // 変形完了後はoriginal状態を更新（次の操作のため）
      // 移動・回転・拡大縮小すべてで更新する
      updateOriginalStateAfterTransform()
    }
  }

  // イベント登録
  map.on('mousedown', handleMouseDown)
  map.on('mousemove', handleMouseMove)
  map.on('mouseup', handleMouseUp)

  // クリーンアップ用に保存
  transformBoxState.cleanup = () => {
    map.off('mousedown', handleMouseDown)
    map.off('mousemove', handleMouseMove)
    map.off('mouseup', handleMouseUp)
  }
}

/**
 * 変形を適用
 */
function applyTransform(scale, rotation) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)
  if (!geojson?.features) return

  const originalFeatures = transformBoxState.originalFeatures
  const centroid = transformBoxState.originalCentroid
  const bbox = transformBoxState.originalBbox

  // 選択された地物を変形
  const transformedIds = new Set(originalFeatures.map(f => f.properties.id))

  const updatedFeatures = geojson.features.map(f => {
    if (!transformedIds.has(f.properties.id)) return f

    const original = originalFeatures.find(o => o.properties.id === f.properties.id)
    if (!original) return f

    // 元の形状から変形
    let transformed = JSON.parse(JSON.stringify(original))
    transformed = turf.transformScale(transformed, scale, { origin: centroid })
    // turf.transformRotateは反時計回りが正なので、符号を反転して直感的な方向に
    transformed = turf.transformRotate(transformed, -rotation, { pivot: centroid })

    return {
      ...f,
      geometry: transformed.geometry,
      properties: {
        ...f.properties,
        _hasBeenModified: true // 変形されたことを記録（SIMA出力で使用）
      }
    }
  })

  // ソース更新
  const updatedGeojson = { type: 'FeatureCollection', features: updatedFeatures }
  source.setData(updatedGeojson)

  // Transform Box表示更新
  const scaledBbox = [
    centroid[0] + (bbox[0] - centroid[0]) * scale,
    centroid[1] + (bbox[1] - centroid[1]) * scale,
    centroid[0] + (bbox[2] - centroid[0]) * scale,
    centroid[1] + (bbox[3] - centroid[1]) * scale,
  ]
  updateTransformBoxDisplay(scaledBbox, centroid, rotation)
}

/**
 * 変形完了後にoriginal状態を更新（移動・回転・拡大縮小すべてで使用）
 */
function updateOriginalStateAfterTransform() {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)
  if (!geojson?.features) return

  // 選択中の地物の現在の状態を取得
  const selected = geojson.features.filter(f => f.properties.lassoSelected === true)
  if (selected.length === 0) return

  // original状態を更新（現在の変形済み状態を新しい基準にする）
  transformBoxState.originalFeatures = JSON.parse(JSON.stringify(selected))

  // bboxとcentroidを再計算
  const fc = turf.featureCollection(selected)
  transformBoxState.originalBbox = turf.bbox(fc)
  transformBoxState.originalCentroid = turf.centroid(fc).geometry.coordinates

  // 回転と拡大縮小の値をリセット（新しい基準からの相対値として）
  transformBoxState.currentRotation = 0
  transformBoxState.currentScale = 1
}

/**
 * 座標配列を再帰的にオフセット
 */
function translateCoordinates(coords, dx, dy) {
  if (typeof coords[0] === 'number') {
    // [lng, lat] の場合
    coords[0] += dx
    coords[1] += dy
  } else {
    // ネストされた配列の場合
    for (let i = 0; i < coords.length; i++) {
      translateCoordinates(coords[i], dx, dy)
    }
  }
}

/**
 * 移動を適用
 */
function applyMove(dx, dy) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource(clickCircleSource.iD)
  const geojson = getSourceData(source)
  if (!geojson?.features) return

  const originalFeatures = transformBoxState.originalFeatures
  const centroid = transformBoxState.originalCentroid
  const bbox = transformBoxState.originalBbox

  // 選択された地物を移動
  const transformedIds = new Set(originalFeatures.map(f => f.properties.id))

  const updatedFeatures = geojson.features.map(f => {
    if (!transformedIds.has(f.properties.id)) return f

    const original = originalFeatures.find(o => o.properties.id === f.properties.id)
    if (!original) return f

    // 元の形状から移動（座標を直接オフセット）
    const translated = JSON.parse(JSON.stringify(original))
    translateCoordinates(translated.geometry.coordinates, dx, dy)

    return {
      ...f,
      geometry: translated.geometry,
      properties: {
        ...f.properties,
        _hasBeenModified: true // 移動されたことを記録（SIMA出力で使用）
      }
    }
  })

  // ソース更新
  const updatedGeojson = { type: 'FeatureCollection', features: updatedFeatures }
  source.setData(updatedGeojson)

  // Transform Box表示更新（移動後のbboxとcentroid）
  const movedBbox = [
    bbox[0] + dx,
    bbox[1] + dy,
    bbox[2] + dx,
    bbox[3] + dy,
  ]
  const movedCentroid = [centroid[0] + dx, centroid[1] + dy]
  updateTransformBoxDisplay(movedBbox, movedCentroid, transformBoxState.currentRotation)
}

/**
 * Transform Boxを非表示
 */
export function hideTransformBox() {
  const map = store.state.map01
  if (!map) return

  transformBoxState.isActive = false

  if (transformBoxState.cleanup) {
    transformBoxState.cleanup()
  }

  const source = map.getSource(TRANSFORM_BOX_SOURCE)
  if (source) {
    source.setData({ type: 'FeatureCollection', features: [] })
  }

  // featureCollectionを現在の座標で同期してからマーカーを再作成
  syncFeatureCollection()
  markerAddAndRemove(true)
}

/**
 * Transform Boxがアクティブかどうか
 */
export function isTransformBoxActive() {
  return transformBoxState.isActive
}
